import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import * as THREE from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useSnapshot } from 'valtio';

import { patchSanitizeNodeName } from '@cartwhl/3d/threejs/patchSanitizeNodeName';
import externalDeleteCharacter from '@cartwhl/common/api/externalDeleteCharacter';
import getExternalCharacters, {
  type ExternalCharacterListItem,
} from '@cartwhl/common/api/getExternalCharacters';
import patchUpdateCharacter from '@cartwhl/common/api/patchUpdateCharacter';
import postCreateCharacterUpload from '@cartwhl/common/api/postCreateCharacterUpload';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import FrontDoor from '@cartwhl/ui/feature/FrontDoor';
import useElementSize from '@cartwhl/ui/hooks/useElementSize';
import CharacterIcon from '@cartwhl/ui/icons/CharacterIcon';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import CopyIcon from '@cartwhl/ui/icons/CopyIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import HamburgerIcon from '@cartwhl/ui/icons/HamburgerIcon';
import HelpIcon from '@cartwhl/ui/icons/HelpIcon';
import HomeIcon from '@cartwhl/ui/icons/HomeIcon';
import InfoIcon from '@cartwhl/ui/icons/InfoIcon';
import LinkIcon from '@cartwhl/ui/icons/LinkIcon';
import RedoIcon from '@cartwhl/ui/icons/RedoIcon';
import UndoIcon from '@cartwhl/ui/icons/UndoIcon';
import Button from '@cartwhl/ui/input/Button';
import Panel, { PANEL_WIDTH } from '@cartwhl/ui/layout/Panel';
import Modal from '@cartwhl/ui/molecules/Modal';

import {
  authStore,
  noticesStore,
  requestAppletSignInModal,
  requestAppletUpgradeModal,
  userStore,
} from '../../../stores/global';
import { CanvasLayoutHeaderContent } from '../../CanvasLayout';
import { PATH as MOTION_EDITOR_PATH } from '../../MotionEditor/constants';
import { createMotionEditorSceneIdFromMotion } from '../../MotionEditor/store/sceneCreateUtils';
import CartographerLayoutStateScaffold from '../Layout/CartographerLayoutStateScaffold';
import {
  type GizmoRotationAxis,
  type LoadedRenderAssetInfo,
  type PropertiesToolMode,
  type RotateSpaceMode,
  type ViewerToolMode,
  type ViewerVisibility,
  getSelectedBoneIndex,
  getSelectedOutlinerNodeId,
  useStore,
} from '../cartographerStore';
import { useCartographerHotkeys } from '../hooks/useCartographerHotkeys';
import {
  coerceOpaqueBlendMaterials,
  collectSceneAssetStats,
  stripMorphTargetsFromScene,
} from '../lib/assetHygiene';
import { buildAssignedBoneIndexSet } from '../lib/assignedBoneSet';
import {
  isCartographerDebugEnabled,
  pushCartographerDebugEvent,
  stringifyDebugReason,
} from '../lib/cartographerDebug';
import {
  readCharactersSessionCache,
  writeCharactersSessionCache,
} from '../lib/charactersSessionCache';
import {
  CartographerFBXLoader,
  type CartographerFbxLoadInfo,
} from '../lib/fbx';
import type { FbxUpAxisMetadata } from '../lib/fbx/fbxAxisSystem';
import {
  getFbxAxisHeuristicWarning,
  getFbxImportWarningSummary,
  getImportWarningReasonText,
} from '../lib/fbx/fbxImportWarnings';
import { prepareCartographerFbxScene } from '../lib/fbx/prepareCartographerFbxScene';
import { exportMappingJson, importMappingJson } from '../lib/io/mappingIO';
import { getCartographerLegSolverAutoSwitches } from '../lib/mapping/legMode';
import { SolverType } from '../lib/schema/solver';
import { buildShortestBonePathRefs } from '../lib/skeleton/bonePath';
import { extractSkeleton } from '../lib/skeleton/skeletonExtractor';
import { getBoneNameClassName } from '../presentations/boneName';
import {
  EMPTY_BONE_PROPERTY_INDICATOR_STATE,
  getBonePropertyIndicatorState,
} from '../presentations/properties';
import BoneAssignments, {
  type BoneAssignmentMode,
  type BoneAssignmentsViewerFocus,
  type BoneAssignmentsViewerPickMode,
  type HoveredBoneAssignmentChain,
} from './BoneAssignments';
import CartographerCharacterPanel, {
  type CartographerCharacterUploadPanelTab,
} from './CharacterLibrary/CartographerCharacterPanel';
import CharacterLibraryLoadModal from './CharacterLibrary/CharacterLibraryLoadModal';
import {
  ENABLE_DOCKED_MINI_MOTION_PREVIEW,
  ENABLE_MOTION_SELECTION_PERSISTENCE,
  useCartographerMotionSection,
} from './CharacterMotion';
import { clearCartographerMotionRetargetPreviewCache } from './CharacterMotion/useCartographerMotionRetargetPreview';
import EditableCharacterFileName from './EditableCharacterFileName';
import CartographerHistoryPanel from './History/CartographerHistoryPanel';
import Inspector from './Inspector';
import Outliner from './Outliner';
import OutlinerSettingsControl from './Outliner/OutlinerSettingsControl';
import useOutlinerControls from './Outliner/useOutlinerControls';
import { ViewerToolDock, ViewerToolbar } from './Viewer';
import Viewer3D, {
  type ViewerFocusRequest,
} from './Viewer/components/Viewer3D';
import CartographerWelcomeScreen from './Welcome';
import { WorkspacePanels, useWorkspaceLayout } from './WorkspaceLayout';
import useCartographerMappingSection from './useCartographerMappingSection';

patchSanitizeNodeName();

const HEADER_OVERHEAD_WIDTH = 72;
const HEADER_FILENAME_MIN_WIDTH = 80;
const HEADER_FILENAME_MAX_WIDTH = 360;
const HEADER_FILENAME_EDITABLE_EXTRA_WIDTH = 32;
const HEADER_FILENAME_HIDE_BUFFER_PX = 32;
const HEADER_ACTIONS_WIDTH_WITH_MAPPING_LABELS = 620;
const HEADER_ACTIONS_WIDTH_WITH_MAPPING_ICONS = 205;
const HEADER_ACTIONS_WIDTH_NO_MAPPING_LABELS = 370;
const HEADER_ACTIONS_WIDTH_NO_MAPPING_ICONS = 120;
const HEADER_OPEN_IN_MOTION_EDITOR_BUTTON_WIDTH = 200;
const CARTOGRAPHER_CANVAS_LAYOUT_PADDING_FALLBACK_PX = 16;
const CARTOGRAPHER_VIEWER_SIDE_MARGIN_PX = 12;
const CARTOGRAPHER_VIEWER_BOTTOM_MARGIN_PX = CARTOGRAPHER_VIEWER_SIDE_MARGIN_PX;
const CARTOGRAPHER_VIEWER_WORKSPACE_STYLE = {
  marginInlineStart: `calc(${CARTOGRAPHER_VIEWER_SIDE_MARGIN_PX}px - var(--canvas-main-px-start, ${CARTOGRAPHER_CANVAS_LAYOUT_PADDING_FALLBACK_PX}px))`,
  marginInlineEnd: `calc(${CARTOGRAPHER_VIEWER_SIDE_MARGIN_PX}px - var(--canvas-main-px-end, ${CARTOGRAPHER_CANVAS_LAYOUT_PADDING_FALLBACK_PX}px))`,
  paddingBottom: CARTOGRAPHER_VIEWER_BOTTOM_MARGIN_PX,
} as CSSProperties;
const CHARACTER_DRAWER_TRANSITION_DURATION_MS = 200;
const SIDE_PANELS_FADE_IN_DELAY_MS = 120;
// Keep PNG until Cartwheel API docs explicitly confirm additional thumbnail formats.
const CHARACTER_THUMBNAIL_EXTENSION = 'png';
const CHARACTER_THUMBNAIL_CONTENT_TYPE = 'image/png';
const CARTOGRAPHER_FBX_LOAD_OPTIONS = {
  ignoreMorphTargets: true,
  lodPolicy: 'highest',
} as const;

function buildPresignedPutOptions(body: Blob | File, contentType: string) {
  return {
    method: 'PUT' as const,
    body,
    headers: { 'Content-Type': contentType },
  };
}

type ViewerPickModeState = {
  mode: BoneAssignmentsViewerPickMode | null;
  order: number;
};

const CARTOGRAPHER_DOCS_URL =
  'https://docs.getcartwheel.com/docs/custom-characters/cartographer';

const CARTOGRAPHER_ACCOUNT_BANNER_SIGNIN_DISMISSED =
  'applets.cartographer.accountBanner.signInDismissed';
const CARTOGRAPHER_ACCOUNT_BANNER_UPGRADE_DISMISSED =
  'applets.cartographer.accountBanner.upgradeDismissed';
const CARTOGRAPHER_ACCOUNT_UPGRADE_URL = getHomepageUrl('/pricing');

type ThumbnailDimensions = {
  width: number | null;
  height: number | null;
};

async function getThumbnailDimensions(
  blob: Blob,
): Promise<ThumbnailDimensions> {
  if (!blob.size) {
    return { width: null, height: null };
  }

  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      const dimensions = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      return dimensions;
    } catch {
      // Fall through to the image-element fallback.
    }
  }

  if (typeof document === 'undefined') {
    return { width: null, height: null };
  }

  return new Promise<ThumbnailDimensions>((resolve) => {
    const objectUrl = URL.createObjectURL(blob);
    const imageElement = document.createElement('img');
    imageElement.onload = () => {
      const width = imageElement.naturalWidth || imageElement.width || null;
      const height = imageElement.naturalHeight || imageElement.height || null;
      URL.revokeObjectURL(objectUrl);
      resolve({ width, height });
    };
    imageElement.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: null, height: null });
    };
    imageElement.src = objectUrl;
  });
}

async function logThumbnailCaptureMetrics(
  mode: 'new' | 'overwrite',
  thumbnailBlob: Blob,
) {
  const { width, height } = await getThumbnailDimensions(thumbnailBlob);
  console.log('[Cartographer][ThumbnailCapture]', {
    mode,
    bytes: thumbnailBlob.size,
    kib: Number((thumbnailBlob.size / 1024).toFixed(1)),
    width,
    height,
  });
}

type InspectorTitleInfo = {
  title: string;
  titleClassName?: string;
};

function resolveInspectorTitleInfo(
  selectedBoneName: string | null,
  isSelectedBoneExcluded: boolean,
): InspectorTitleInfo {
  if (!selectedBoneName) {
    return { title: 'Inspector' };
  }
  return {
    title: selectedBoneName,
    titleClassName: isSelectedBoneExcluded
      ? getBoneNameClassName('excluded')
      : undefined,
  };
}

type CombinedPanelTab = 'outliner' | 'detail';
type DetailPanelMode = 'inspector' | 'boneAssignments' | 'history';
type CharacterSwitchPhase = 'idle' | 'priming' | 'loading';
type CharacterSaveMarker = {
  fileVersion: number;
  fileName: string | null;
  mappingVersion: number;
  characterID: string | null;
};
type CharacterSaveMode = 'overwrite' | 'new';
type CharacterSaveOptions = {
  mode?: CharacterSaveMode;
};
type PendingCharacterLoadAction = () => void | boolean | Promise<boolean>;
type PendingExcludedAssignment =
  | {
      kind: 'role';
      excludedBoneRefs: string[];
      boneRef: string;
      nextRole: string;
    }
  | {
      kind: 'chainAdd';
      excludedBoneRefs: string[];
      chainName: string;
      boneRef: string;
    }
  | {
      kind: 'chainPath';
      excludedBoneRefs: string[];
      chainName: string;
      startBoneRef: string;
      endBoneRef: string;
    };

function enableShadowsOnScene(scene: THREE.Group) {
  scene.traverse((object) => {
    object.castShadow = true;
    object.receiveShadow = true;
  });
}

function formatCharacterFileSize(bytes: number): string | null {
  if (!Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function waitForNextPaint(): Promise<void> {
  if (
    typeof window === 'undefined' ||
    typeof window.requestAnimationFrame !== 'function'
  ) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.setTimeout(resolve, 0);
    });
  });
}

function getCartographerFbxLoadInfo(
  scene: THREE.Object3D,
): CartographerFbxLoadInfo | null {
  const loadInfo = scene.userData.cartographerFbxLoadInfo;
  return loadInfo &&
    typeof loadInfo === 'object' &&
    'selectedLodLabel' in loadInfo
    ? (loadInfo as CartographerFbxLoadInfo)
    : null;
}

function toLoadedRenderAssetInfo(
  loadInfo: CartographerFbxLoadInfo | null,
): LoadedRenderAssetInfo | null {
  if (!loadInfo) return null;

  return {
    lodPolicy: loadInfo.lodPolicy === 'highest' ? 'highest' : null,
    selectedLodLabel: loadInfo.selectedLodLabel,
    skippedLodLabels: [...new Set(loadInfo.skippedLodLabels)],
    skippedLodMeshNames: [...loadInfo.skippedLodMeshNames],
    morphTargetsIgnored: loadInfo.morphTargetsIgnored,
  };
}

export default function Cartographer() {
  const store = useStore();
  const { pathname, search } = useLocation();
  const {
    fileName,
    fileVersion,
    mappingVersion,
    hasGltfScene,
    hasSkeleton,
    hasMapping,
    renderAssetInfo,
    chainSummaries,
    presetName,
    loading,
    error,
    warning,
    visibility,
    toolbarMenuMode,
    viewerToolMode,
    rotateSpaceMode,
    propertiesToolMode,
    gridCellSize,
    boneColorPreset,
    boneRadiusScale,
    chainThicknessScale,
    manipulatorScale,
    cameraFollowMotion,
    selection,
    selectionVersion,
    poseVersion,
    bonePropertiesVersion,
    excludedBoneNames,
    importWarningDetails,
    historyItems,
    historyIndex,
  } = store.state;
  const mappingInputRef = useRef<HTMLInputElement>(null);
  const fileNameMeasureRef = useRef<HTMLDivElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  /** Import JSON queued until skeleton exists (avoids async load overwriting import with auto-mapping). */
  const pendingMappingImportJsonRef = useRef<string | null>(null);
  /**
   * When true, mapping JSON came from Load Character (configURL prefetch).
   * Import failures must not fall back to recognizeSkeleton.
   */
  const characterConfigImportStrictRef = useRef(false);
  /** Bumps when a new file load starts so stale config fetches are ignored. */
  const modelLoadGenerationRef = useRef(0);
  /**
   * Character loaded from API; mark clean after scene + mapping import settles.
   */
  const pendingLoadedCharacterBaselineRef = useRef<string | null>(null);
  const [
    pendingLoadedCharacterBaselineID,
    setPendingLoadedCharacterBaselineID,
  ] = useState<string | null>(null);
  const setPendingLoadedCharacterBaseline = useCallback(
    (characterID: string | null) => {
      pendingLoadedCharacterBaselineRef.current = characterID;
      setPendingLoadedCharacterBaselineID(characterID);
    },
    [],
  );
  // Keeps Viewer3D, header buttons, and panels mounted across character
  // transitions so they don't flash/rebuild during loading.
  const hasEverLoadedScene = useRef(false);
  if (hasGltfScene) hasEverLoadedScene.current = true;
  const [fileNameTextWidth, setFileNameTextWidth] = useState(0);
  const [detailPanelMode, setDetailPanelMode] =
    useState<DetailPanelMode>('inspector');
  const [boneAssignmentsTitle, setBoneAssignmentsTitle] =
    useState('Bone Assignments');
  const [boneAssignmentsViewerRoleTag, setBoneAssignmentsViewerRoleTag] =
    useState<{
      boneIndex: number;
      role: string;
      confidence: number | null;
    } | null>(null);
  const [boneAssignmentsViewerFocus, setBoneAssignmentsViewerFocus] =
    useState<BoneAssignmentsViewerFocus | null>(null);
  const [boneAssignmentsHoveredChain, setBoneAssignmentsHoveredChain] =
    useState<HoveredBoneAssignmentChain | null>(null);
  const [isViewerHovered, setIsViewerHovered] = useState(false);
  const [isViewerFocused, setIsViewerFocused] = useState(false);
  const [
    boneAssignmentsViewerPickModeState,
    setBoneAssignmentsViewerPickModeState,
  ] = useState<ViewerPickModeState>({ mode: null, order: 0 });
  const [outlinerViewerPickModeState, setOutlinerViewerPickModeState] =
    useState<ViewerPickModeState>({ mode: null, order: 0 });
  const [
    boneAssignmentsClearSelectionToken,
    setBoneAssignmentsClearSelectionToken,
  ] = useState(0);
  const [boneAssignmentsMode, setBoneAssignmentsMode] =
    useState<BoneAssignmentMode>('roles');
  const [isImportWarningDetailsOpen, setIsImportWarningDetailsOpen] =
    useState(false);
  const [isCharacterDrawerOpen, setIsCharacterDrawerOpen] = useState(false);
  const [characterDrawerUploadPanelOpen, setCharacterDrawerUploadPanelOpen] =
    useState(false);
  const [characterDrawerUploadPanelTab, setCharacterDrawerUploadPanelTab] =
    useState<CartographerCharacterUploadPanelTab>('upload');
  const characterDrawerUploadPanelOpenRef = useRef(
    characterDrawerUploadPanelOpen,
  );
  characterDrawerUploadPanelOpenRef.current = characterDrawerUploadPanelOpen;
  const characterDrawerUploadPreparingRef = useRef(false);
  const handleUploadPanelPreparingChange = useCallback((preparing: boolean) => {
    characterDrawerUploadPreparingRef.current = preparing;
  }, []);
  const [viewerFocusRequest, setViewerFocusRequest] =
    useState<ViewerFocusRequest | null>(null);
  const isJointOffsetPreviewActive =
    viewerToolMode === 'properties' && propertiesToolMode === 'offset';

  useEffect(() => {
    if (importWarningDetails.length === 0 || !warning) {
      setIsImportWarningDetailsOpen(false);
    }
  }, [warning, importWarningDetails.length]);
  useEffect(() => {
    store.setJointOffsetPreviewActive(isJointOffsetPreviewActive);
  }, [isJointOffsetPreviewActive, store]);
  const [sidePanelsVisible, setSidePanelsVisible] = useState(true);
  const [characterSwitchPhase, setCharacterSwitchPhase] =
    useState<CharacterSwitchPhase>('idle');
  const [hasObservedSwitchLoading, setHasObservedSwitchLoading] =
    useState(false);

  const [loadCharacterModalOpen, setLoadCharacterModalOpen] = useState(false);
  const [charactersList, setCharactersList] = useState<
    ExternalCharacterListItem[]
  >([]);
  const [charactersLoading, setCharactersLoading] = useState(false);
  const [charactersError, setCharactersError] = useState<string | null>(null);
  const [loadingCharacterId, setLoadingCharacterId] = useState<string | null>(
    null,
  );
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [saveInProgressMode, setSaveInProgressMode] =
    useState<CharacterSaveMode | null>(null);
  /** When set, we loaded this character from the API; Save will PATCH and upload config/thumbnail only. */
  const [loadedCharacterID, setLoadedCharacterID] = useState<string | null>(
    null,
  );
  const [lastSavedMarker, setLastSavedMarker] =
    useState<CharacterSaveMarker | null>(null);
  const pendingCharacterLoadActionRef =
    useRef<PendingCharacterLoadAction | null>(null);
  const [loadCharacterUnsavedModalOpen, setLoadCharacterUnsavedModalOpen] =
    useState(false);
  const [loadCharacterSaveInProgress, setLoadCharacterSaveInProgress] =
    useState(false);
  const [pendingCharacterLoadTargetLabel, setPendingCharacterLoadTargetLabel] =
    useState('this character');
  const [pendingExcludedAssignment, setPendingExcludedAssignment] =
    useState<PendingExcludedAssignment | null>(null);
  const cartographerFrontDoorRef = useRef<HTMLDivElement | null>(null);
  const [isViewerToolDockMounted, setIsViewerToolDockMounted] = useState(false);
  const charactersFetchIdRef = useRef(0);
  const charactersCacheApiKeyRef = useRef<string | null>(null);
  const charactersLoadedOnceRef = useRef(false);
  const debugLifecycleSignatureRef = useRef('');
  const viewerVisibilityBeforeBoneAssignmentsRef = useRef<Pick<
    ViewerVisibility,
    'showRoles' | 'showChains' | 'showUnassignedBones'
  > | null>(null);
  const {
    width: cartographerFrontDoorWidth,
    height: cartographerFrontDoorHeight,
  } = useElementSize(cartographerFrontDoorRef);
  const screenshotRef = useRef<(() => Promise<Blob>) | null>(null);
  const handleSaveRef = useRef<() => Promise<boolean>>(async () => false);
  const navigate = useNavigate();
  const [openingMotionEditor, setOpeningMotionEditor] = useState(false);
  const viewerPickModeOrderRef = useRef(0);
  const handleViewerToolDockMount = useCallback(
    (node: HTMLDivElement | null) => {
      setIsViewerToolDockMounted(Boolean(node));
    },
    [],
  );

  const { apiKey: apiKeyState } = useSnapshot(userStore.state);
  const apiKey =
    apiKeyState.status === 'ready' ? (apiKeyState.data?.apiKey ?? null) : null;
  const organizationServiceId =
    apiKeyState.status === 'ready'
      ? (apiKeyState.data?.serviceID ?? null)
      : null;

  const { authStatus } = useSnapshot(authStore.state);

  const accountAccessLoginUrl = useMemo(() => {
    const redirectTo = `apps${pathname}${search}`;
    return getHomepageUrl(
      `/login?redirectTo=${encodeURIComponent(redirectTo)}`,
    );
  }, [pathname, search]);

  const [accountSignInBannerDismissed, setAccountSignInBannerDismissed] =
    useState(
      () =>
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem(CARTOGRAPHER_ACCOUNT_BANNER_SIGNIN_DISMISSED) ===
          '1',
    );

  const [accountUpgradeBannerDismissed, setAccountUpgradeBannerDismissed] =
    useState(
      () =>
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem(
          CARTOGRAPHER_ACCOUNT_BANNER_UPGRADE_DISMISSED,
        ) === '1',
    );

  const dismissAccountSignInBanner = useCallback(() => {
    sessionStorage.setItem(CARTOGRAPHER_ACCOUNT_BANNER_SIGNIN_DISMISSED, '1');
    setAccountSignInBannerDismissed(true);
  }, []);

  const dismissAccountUpgradeBanner = useCallback(() => {
    sessionStorage.setItem(CARTOGRAPHER_ACCOUNT_BANNER_UPGRADE_DISMISSED, '1');
    setAccountUpgradeBannerDismissed(true);
  }, []);

  const showAccountSignInBanner =
    authStatus !== 'user' && !accountSignInBannerDismissed;
  const showAccountUpgradeBanner =
    authStatus === 'user' &&
    apiKeyState.status === 'ready' &&
    !apiKey &&
    !accountUpgradeBannerDismissed;
  const showAccountAccessBanner =
    showAccountSignInBanner || showAccountUpgradeBanner;

  /** Only characters that have gone through Cartographer (have config) are shown in the Load modal. */
  const charactersWithConfig = useMemo(
    () => charactersList.filter((c) => c.configURL),
    [charactersList],
  );
  const skeleton = store.getSkeleton();
  const hasRenderableSkeleton = Boolean(hasSkeleton && skeleton);
  const outlinerHierarchy = store.getOutlinerHierarchy();
  const mapping = store.getMappingResult();
  const mappingHasAssignments = useMemo(() => {
    if (!mapping) return false;
    return (
      Object.keys(mapping.roleToBone).length > 0 ||
      Object.keys(mapping.chains).length > 0
    );
  }, [mapping]);
  const effectiveExcludedBoneRefs = useMemo(() => {
    const merged = new Set<string>();
    for (const ref of mapping?.excludedBones ?? []) {
      merged.add(ref);
    }
    for (const ref of excludedBoneNames) {
      merged.add(ref);
    }
    return [...merged];
  }, [excludedBoneNames, mapping?.excludedBones]);
  const effectiveExcludedBoneRefSet = useMemo(
    () => new Set(effectiveExcludedBoneRefs),
    [effectiveExcludedBoneRefs],
  );
  const effectiveExcludedBoneIndexSet = useMemo(
    () =>
      new Set<number>(
        effectiveExcludedBoneRefs
          .map(
            (ref) =>
              skeleton?.resolveBoneRef(ref, { namespace: 'internal' }) ?? null,
          )
          .filter((idx): idx is number => idx !== null),
      ),
    [effectiveExcludedBoneRefs, skeleton],
  );
  const assignedBoneIndexSet = useMemo(
    () => buildAssignedBoneIndexSet(mapping, skeleton),
    [mapping, skeleton],
  );
  const selectedBoneIndex = getSelectedBoneIndex(selection);
  const selectedOutlinerNodeId = useMemo(
    () => getSelectedOutlinerNodeId(selection, outlinerHierarchy),
    [outlinerHierarchy, selection],
  );
  const selectedOutlinerNode = useMemo(() => {
    if (!selectedOutlinerNodeId || !outlinerHierarchy) return null;
    return outlinerHierarchy.nodesById[selectedOutlinerNodeId] ?? null;
  }, [outlinerHierarchy, selectedOutlinerNodeId]);
  const selectedBone =
    selectedBoneIndex !== null
      ? (skeleton?.bones[selectedBoneIndex] ?? null)
      : null;
  const selectedBonePropertyIndicators = selectedBone
    ? getBonePropertyIndicatorState(selectedBone, mapping?.ioExtras ?? {})
    : EMPTY_BONE_PROPERTY_INDICATOR_STATE;
  const selectedBoneName = selectedBone?.name ?? null;
  const isOffsetPosePreviewActive =
    viewerToolMode === 'properties' && propertiesToolMode === 'offset';
  const selectedInspectorName = selectedOutlinerNode?.name ?? selectedBoneName;
  const isHistoryActive = detailPanelMode === 'history';
  const activeBoneAssignmentsMode: BoneAssignmentMode | null =
    detailPanelMode === 'boneAssignments' ? boneAssignmentsMode : null;
  const isBoneAssignmentsActive = activeBoneAssignmentsMode !== null;
  const isUndoDisabled = historyIndex <= 0;
  const isRedoDisabled =
    historyIndex < 0 || historyIndex >= historyItems.length - 1;
  const showHistoryDockButton = isHistoryActive || historyItems.length > 1;
  const activeViewerPickMode = useMemo(() => {
    const boneAssignmentsPickMode = boneAssignmentsViewerPickModeState.mode;
    const outlinerPickMode = outlinerViewerPickModeState.mode;
    if (boneAssignmentsPickMode === null) return outlinerPickMode;
    if (outlinerPickMode === null) return boneAssignmentsPickMode;
    return boneAssignmentsViewerPickModeState.order >=
      outlinerViewerPickModeState.order
      ? boneAssignmentsPickMode
      : outlinerPickMode;
  }, [boneAssignmentsViewerPickModeState, outlinerViewerPickModeState]);
  const isSelectedBoneExcluded =
    selectedOutlinerNode?.kind === 'bone'
      ? effectiveExcludedBoneIndexSet.has(selectedOutlinerNode.boneIndex)
      : selectedBone != null &&
        effectiveExcludedBoneIndexSet.has(selectedBone.index);
  const inspectorTitleInfo = useMemo(
    () =>
      resolveInspectorTitleInfo(selectedInspectorName, isSelectedBoneExcluded),
    [isSelectedBoneExcluded, selectedInspectorName],
  );
  const detailPanelTitleInfo = useMemo(
    () =>
      isHistoryActive
        ? { title: 'History' }
        : isBoneAssignmentsActive
          ? { title: boneAssignmentsTitle }
          : inspectorTitleInfo,
    [
      boneAssignmentsTitle,
      inspectorTitleInfo,
      isBoneAssignmentsActive,
      isHistoryActive,
    ],
  );
  const detailPanelTitle = detailPanelTitleInfo.title;
  const detailPanelTitleClassName = detailPanelTitleInfo.titleClassName;
  const currentSaveMarker = useMemo<CharacterSaveMarker | null>(() => {
    if (!store.getFile()) return null;
    return {
      fileVersion,
      fileName,
      mappingVersion,
      characterID: loadedCharacterID,
    };
  }, [fileName, fileVersion, loadedCharacterID, mappingVersion, store]);
  const isCharacterDirty = useMemo(() => {
    if (!currentSaveMarker) return false;
    // Library character switches update file/mapping before the saved baseline
    // marker is captured, which can briefly look dirty. Keep Save hidden until
    // that baseline is resolved.
    if (pendingLoadedCharacterBaselineID) {
      return false;
    }
    // Existing API characters can still be loading/importing their saved config.
    // Until baseline is captured, treat them as clean to avoid false save prompts.
    if (!lastSavedMarker) return currentSaveMarker.characterID === null;
    return (
      currentSaveMarker.fileVersion !== lastSavedMarker.fileVersion ||
      currentSaveMarker.fileName !== lastSavedMarker.fileName ||
      currentSaveMarker.mappingVersion !== lastSavedMarker.mappingVersion ||
      currentSaveMarker.characterID !== lastSavedMarker.characterID
    );
  }, [currentSaveMarker, lastSavedMarker, pendingLoadedCharacterBaselineID]);
  const dirtyDebugSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const reasonParts: string[] = [];
    if (!currentSaveMarker) {
      reasonParts.push('no-current-save-marker');
    } else if (pendingLoadedCharacterBaselineID) {
      reasonParts.push(
        `pending-loaded-baseline:${pendingLoadedCharacterBaselineID}`,
      );
    } else if (!lastSavedMarker) {
      reasonParts.push(
        currentSaveMarker.characterID === null
          ? 'no-last-saved-marker:local-file-dirty'
          : 'no-last-saved-marker:library-character-clean',
      );
    } else {
      if (currentSaveMarker.fileVersion !== lastSavedMarker.fileVersion) {
        reasonParts.push(
          `fileVersion ${lastSavedMarker.fileVersion} -> ${currentSaveMarker.fileVersion}`,
        );
      }
      if (currentSaveMarker.fileName !== lastSavedMarker.fileName) {
        reasonParts.push(
          `fileName ${lastSavedMarker.fileName ?? 'null'} -> ${currentSaveMarker.fileName ?? 'null'}`,
        );
      }
      if (currentSaveMarker.mappingVersion !== lastSavedMarker.mappingVersion) {
        reasonParts.push(
          `mappingVersion ${lastSavedMarker.mappingVersion} -> ${currentSaveMarker.mappingVersion}`,
        );
      }
      if (currentSaveMarker.characterID !== lastSavedMarker.characterID) {
        reasonParts.push(
          `characterID ${lastSavedMarker.characterID ?? 'null'} -> ${currentSaveMarker.characterID ?? 'null'}`,
        );
      }
      if (reasonParts.length === 0) {
        reasonParts.push('clean-marker-match');
      }
    }

    const nextSignature = JSON.stringify({
      isCharacterDirty,
      currentSaveMarker,
      lastSavedMarker,
      pendingLoadedCharacterBaselineID,
      reasonParts,
    });
    if (dirtyDebugSignatureRef.current === nextSignature) return;
    dirtyDebugSignatureRef.current = nextSignature;

    console.log('[Cartographer][DirtyState]', {
      isCharacterDirty,
      reason: reasonParts.join(' | '),
      currentSaveMarker,
      lastSavedMarker,
      pendingLoadedCharacterBaselineID,
      loadingCharacterId,
      loadedCharacterID,
      fileVersion,
      mappingVersion,
    });
  }, [
    currentSaveMarker,
    fileVersion,
    isCharacterDirty,
    lastSavedMarker,
    loadedCharacterID,
    loadingCharacterId,
    mappingVersion,
    pendingLoadedCharacterBaselineID,
  ]);
  const topSaveButtonLabel = saveInProgress
    ? 'Saving…'
    : loadedCharacterID
      ? 'Save'
      : 'Save as new character';
  const menuSaveButtonLabel =
    saveInProgressMode === 'overwrite' ? 'Saving…' : 'Save';
  const menuSaveAsNewButtonLabel =
    saveInProgressMode === 'new' ? 'Saving…' : 'Save as new character';
  const topSaveButtonDisabled =
    saveInProgress || !hasGltfScene || !isCharacterDirty;
  const menuSaveButtonDisabled = saveInProgress;
  const primarySaveMode: CharacterSaveMode = loadedCharacterID
    ? 'overwrite'
    : 'new';
  const showTopSaveButton =
    isCharacterDirty &&
    (saveInProgressMode === null || saveInProgressMode === primarySaveMode);
  const showMenuSaveButton = loadedCharacterID !== null;
  const activeCharacterFile = store.getFile();
  const activeCharacterFileSizeLabel = activeCharacterFile
    ? formatCharacterFileSize(activeCharacterFile.size)
    : null;
  const activeRenderLodLabel =
    renderAssetInfo?.lodPolicy === 'highest' && renderAssetInfo.selectedLodLabel
      ? `Highest (${renderAssetInfo.selectedLodLabel})`
      : null;
  const clearStoreError = useCallback(() => {
    store.setError(null);
  }, [store]);
  const setStoreError = useCallback(
    (message: string) => {
      store.setError(message);
    },
    [store],
  );
  const exportCurrentMappingConfig = useCallback(() => {
    const currentMapping = store.getMappingResult();
    if (!currentMapping) return null;
    return exportMappingJson(currentMapping, store.getSkeleton());
  }, [store]);
  const showEditorChrome = hasGltfScene || hasEverLoadedScene.current;
  const showWelcomeScreen =
    !loading && !hasGltfScene && !hasEverLoadedScene.current;
  const showCharacterDrawer = showEditorChrome && isCharacterDrawerOpen;
  const [isHeaderFileNameVisible, setIsHeaderFileNameVisible] = useState(false);
  const isCharacterSwitchActive = characterSwitchPhase !== 'idle';
  const {
    canRenderMotionMini,
    cartographerAssetLibraryMode,
    assetLibraryPanel,
    motionActorPickerModal,
    motionPreviewModal,
    motionPreviewSurface,
    motionStagePlaybackActive,
    motionStagePreview,
    motionSection,
    motionUnsavedChangesModal,
    resetMotionMiniState,
    selectedMotionId,
    selectedMotionLabel,
    selectedBodyIndex,
    isMotionRetargeting,
  } = useCartographerMotionSection({
    apiKey,
    organizationServiceId,
    loadedCharacterID,
    activeCharacterFile,
    hasGltfScene,
    isViewerToolDockMounted,
    isCharacterDirty,
    frontDoorRef: cartographerFrontDoorRef,
    frontDoorHeight: cartographerFrontDoorHeight,
    frontDoorWidth: cartographerFrontDoorWidth,
    fileVersion,
    mappingVersion,
    bonePropertiesVersion,
    fileName,
    loading,
    saveInProgress,
    isCharacterSwitchActive,
    handleSaveRef,
    onClearError: clearStoreError,
    onSetError: setStoreError,
    exportCurrentMappingConfig,
    buildPresignedPutOptions,
  });

  const activeViewportMode = showWelcomeScreen
    ? 'welcome'
    : loading && !hasEverLoadedScene.current
      ? 'initial-loading'
      : hasGltfScene || hasEverLoadedScene.current
        ? 'viewer'
        : 'empty';
  const showAssetLibrary = cartographerAssetLibraryMode === 'motions';
  const panelsBlocked =
    showCharacterDrawer || showAssetLibrary || isCharacterSwitchActive;
  const showSidePanels = !panelsBlocked && sidePanelsVisible;
  const hideSidePanels = !showSidePanels;
  const {
    activeCombinedTab,
    combinedPanelHeight,
    combinedPanelMaxWidth,
    combinedPanelMinWidth,
    combinedPanelResolvedWidth,
    getCombinedMinWidthForTab,
    handleCombinedPanelWidthChange,
    handleCombinedTabSelect,
    handleInspectorWidthChange,
    handleOutlinerMinContentWidthChange,
    handleOutlinerWidthChange,
    inspectorHeight,
    inspectorMaxWidth,
    inspectorWidth,
    isCombinedPanelOpen,
    isOutlinerOpen,
    isOutlinerPanelInteractable,
    isInspectorOpen,
    outlinerHeight,
    outlinerMaxWidth,
    outlinerMinWidth,
    outlinerWidth,
    setActiveCombinedTab,
    setCombinedPanelHeight,
    setCombinedPanelWidth,
    setIsCombinedPanelOpen,
    setIsOutlinerOpen,
    setInspectorHeight,
    setIsInspectorOpen,
    setOutlinerHeight,
    showToolbarTriggerLabels,
    usesCombinedLayout,
    viewportWidth,
  } = useWorkspaceLayout({
    hasRenderableSkeleton,
    showSidePanels,
  });
  const isOutlinerSettingsAvailable =
    hasRenderableSkeleton &&
    (usesCombinedLayout
      ? isCombinedPanelOpen && activeCombinedTab === 'outliner'
      : isOutlinerOpen);
  const {
    handleRevealSelectedBone,
    hotkeysEnabled,
    outlinerProps,
    settingsControlProps,
  } = useOutlinerControls({
    columnLockMode: activeBoneAssignmentsMode,
    hasRenderableSkeleton,
    isOutlinerPanelInteractable,
    isOutlinerSettingsAvailable,
    selectedBoneIndex,
  });
  const viewerFocusRequestTokenRef = useRef(0);
  const viewerHotkeysEnabled =
    hasGltfScene && (isViewerHovered || isViewerFocused);
  const activeFocusSurface = hotkeysEnabled
    ? 'outliner'
    : hasGltfScene && isViewerHovered
      ? 'viewer-hover'
      : viewerHotkeysEnabled
        ? 'viewer-focus'
        : null;
  const enqueueViewerFocusRequest = useCallback(
    (target: ViewerFocusRequest['target']) => {
      viewerFocusRequestTokenRef.current += 1;
      setViewerFocusRequest({
        token: viewerFocusRequestTokenRef.current,
        target,
        fallbackBoneIndex: selectedBoneIndex ?? null,
      });
    },
    [selectedBoneIndex],
  );
  const handleFocusHotkey = useCallback(() => {
    if (activeFocusSurface === 'outliner') {
      handleRevealSelectedBone();
      return;
    }
    if (activeFocusSurface === null) return;
    if (boneAssignmentsViewerFocus) {
      enqueueViewerFocusRequest({
        kind: 'bone-assignments',
        focus: boneAssignmentsViewerFocus,
      });
      return;
    }
    if (selectedBoneIndex !== null) {
      enqueueViewerFocusRequest({
        kind: 'bone',
        boneIndex: selectedBoneIndex,
      });
      return;
    }
    enqueueViewerFocusRequest({ kind: 'scene' });
  }, [
    activeFocusSurface,
    boneAssignmentsViewerFocus,
    enqueueViewerFocusRequest,
    handleRevealSelectedBone,
    selectedBoneIndex,
  ]);
  const showMappingActions =
    hasMapping || (hasEverLoadedScene.current && !hasGltfScene);
  const showOpenInMotionEditorButton = Boolean(
    showEditorChrome && selectedMotionId && !isMotionRetargeting,
  );
  const isHeaderFileNameEditable = Boolean(apiKey && hasGltfScene);
  const headerActionsWithLabelsWidth =
    (showMappingActions
      ? HEADER_ACTIONS_WIDTH_WITH_MAPPING_LABELS
      : HEADER_ACTIONS_WIDTH_NO_MAPPING_LABELS) +
    (showOpenInMotionEditorButton
      ? HEADER_OPEN_IN_MOTION_EDITOR_BUTTON_WIDTH
      : 0);
  const headerActionsIconOnlyWidth =
    (showMappingActions
      ? HEADER_ACTIONS_WIDTH_WITH_MAPPING_ICONS
      : HEADER_ACTIONS_WIDTH_NO_MAPPING_ICONS) +
    (showOpenInMotionEditorButton
      ? HEADER_OPEN_IN_MOTION_EDITOR_BUTTON_WIDTH
      : 0);
  const fileNameLayoutWidth = fileName
    ? Math.min(
        HEADER_FILENAME_MAX_WIDTH,
        Math.max(
          HEADER_FILENAME_MIN_WIDTH,
          Math.ceil(fileNameTextWidth || HEADER_FILENAME_MIN_WIDTH) +
            (isHeaderFileNameEditable
              ? HEADER_FILENAME_EDITABLE_EXTRA_WIDTH
              : 0),
        ),
      )
    : 0;

  useEffect(() => {
    if (!isCartographerDebugEnabled()) return;

    pushCartographerDebugEvent('debug.session.start', {
      hasApiKey: Boolean(apiKey),
      loadedCharacterID,
      enableDockedMiniMotionPreview: ENABLE_DOCKED_MINI_MOTION_PREVIEW,
      enableMotionSelectionPersistence: ENABLE_MOTION_SELECTION_PERSISTENCE,
    });

    const handleWindowError = (event: ErrorEvent) => {
      pushCartographerDebugEvent('window.error', {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      pushCartographerDebugEvent('window.unhandledrejection', {
        reason: stringifyDebugReason(event.reason),
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, [apiKey, loadedCharacterID]);

  useEffect(() => {
    return () => {
      clearCartographerMotionRetargetPreviewCache();
    };
  }, []);

  useEffect(() => {
    if (!isCartographerDebugEnabled()) return;
    const signature = JSON.stringify({
      loading,
      hasGltfScene,
      hasEverLoadedScene: hasEverLoadedScene.current,
      loadedCharacterID,
      loadingCharacterId,
      characterSwitchPhase,
      isCharacterSwitchActive,
      activeViewportMode,
      showWelcomeScreen,
      showEditorChrome,
      motionPreviewSurface,
      selectedMotionId,
      canRenderMotionMini,
    });
    if (debugLifecycleSignatureRef.current === signature) return;
    debugLifecycleSignatureRef.current = signature;
    pushCartographerDebugEvent('state.lifecycle', {
      loading,
      hasGltfScene,
      hasEverLoadedScene: hasEverLoadedScene.current,
      loadedCharacterID,
      loadingCharacterId,
      characterSwitchPhase,
      isCharacterSwitchActive,
      activeViewportMode,
      showWelcomeScreen,
      showEditorChrome,
      motionPreviewSurface,
      selectedMotionId,
      canRenderMotionMini,
    });
  }, [
    canRenderMotionMini,
    characterSwitchPhase,
    hasGltfScene,
    activeViewportMode,
    isCharacterSwitchActive,
    loadedCharacterID,
    loading,
    loadingCharacterId,
    motionPreviewSurface,
    selectedMotionId,
    showEditorChrome,
    showWelcomeScreen,
  ]);
  const fullHeaderWidthNeeded =
    HEADER_OVERHEAD_WIDTH + headerActionsWithLabelsWidth + fileNameLayoutWidth;
  const headerFileNameWidthNeeded =
    HEADER_OVERHEAD_WIDTH + headerActionsIconOnlyWidth + fileNameLayoutWidth;
  const showActionLabels = viewportWidth >= fullHeaderWidthNeeded;
  const shouldRenderHeaderFileName =
    viewportWidth >= headerFileNameWidthNeeded && Boolean(fileName);
  const shouldAttemptHeaderFileName =
    showEditorChrome && shouldRenderHeaderFileName && Boolean(fileName);
  const shouldRenderDrawerFileName =
    showCharacterDrawer && Boolean(fileName) && !isHeaderFileNameVisible;
  const actionAlignIcon = showActionLabels ? 'end' : 'start';

  useEffect(() => {
    if (!shouldAttemptHeaderFileName) {
      setIsHeaderFileNameVisible(false);
    }
  }, [shouldAttemptHeaderFileName]);

  const handleOutlinerNodeSelect = useCallback(
    (nodeId: string | null, boneIndex: number | null) => {
      if (boneIndex !== null) {
        store.selectBone(boneIndex);
        return;
      }
      if (!nodeId) {
        store.clearSelection();
        return;
      }
      store.selectTransformNode(nodeId);
    },
    [store],
  );
  const handleViewerPropertiesBadgeSelect = useCallback(
    (index: number) => {
      store.selectBone(index);
      if (viewerToolMode !== 'properties') {
        store.setViewerToolMode('properties');
      }
    },
    [store, viewerToolMode],
  );
  const recordMappingHistoryAction = useCallback(
    (label: string, action: () => void) => {
      store.recordHistory(label, 'mapping', action);
    },
    [store],
  );
  const recordLegModeAwareMappingHistoryAction = useCallback(
    (label: string, action: () => void) => {
      const previousLegModes = store.captureLegModeSnapshot();
      recordMappingHistoryAction(label, action);

      const nextLegModes = store.captureLegModeSnapshot();
      const solverSwitches = getCartographerLegSolverAutoSwitches(
        previousLegModes,
        nextLegModes,
      );
      for (const solverSwitch of solverSwitches) {
        const solverHistoryLabel =
          solverSwitch.solverType === SolverType.Z
            ? 'Auto-switch leg solver to ZSolver'
            : 'Auto-switch leg solver to TriSolver';
        recordMappingHistoryAction(solverHistoryLabel, () =>
          store.setChainSolverType(
            solverSwitch.chainName,
            solverSwitch.solverType,
          ),
        );
      }
    },
    [recordMappingHistoryAction, store],
  );
  const recordPoseHistoryAction = useCallback(
    (label: string, action: () => void) => {
      store.recordHistory(label, 'pose', action);
    },
    [store],
  );
  const recordBonePropertiesHistoryAction = useCallback(
    (label: string, action: () => void) => {
      store.recordHistory(label, 'bone-properties', action);
    },
    [store],
  );
  const beginPoseHistoryTransaction = useCallback(
    (label: string) => {
      store.beginHistoryTransaction(label, 'pose');
    },
    [store],
  );
  const beginBonePropertiesHistoryTransaction = useCallback(
    (label: string) => {
      store.beginHistoryTransaction(label, 'bone-properties');
    },
    [store],
  );
  const commitHistoryTransaction = useCallback(() => {
    store.commitHistoryTransaction();
  }, [store]);
  const handleUndo = useCallback(() => {
    store.undo();
  }, [store]);
  const handleRedo = useCallback(() => {
    store.redo();
  }, [store]);
  const handleBonePropertiesChange = useCallback(() => {
    store.markBonePropertiesChanged('bone-properties');
  }, [store]);
  const handleJointOffsetChange = useCallback(
    (boneRef: string, rotation: [number, number, number] | null) => {
      store.setJointOffsetRotationForBone(boneRef, rotation);
    },
    [store],
  );
  const handleApplyBoneRotateFromWorldQuaternion = useCallback(
    (boneIndex: number, worldQuaternion: THREE.Quaternion) => {
      store.applyBoneRotateFromWorldQuaternion(boneIndex, worldQuaternion);
    },
    [store],
  );
  const handleApplyBoneRotateFromGimbalDelta = useCallback(
    (boneIndex: number, axis: GizmoRotationAxis, deltaDegrees: number) => {
      store.applyBoneRotateFromGimbalDelta(boneIndex, axis, deltaDegrees);
    },
    [store],
  );
  const handleSetBoneEulerRotation = useCallback(
    (boneIndex: number, eulerDegrees: [number, number, number]) => {
      store.setBoneEulerRotation(boneIndex, eulerDegrees);
    },
    [store],
  );
  const handleRotateGizmoDraggingChange = useCallback(
    (isDragging: boolean) => {
      if (isDragging) {
        beginPoseHistoryTransaction('Rotate bone');
        return;
      }
      commitHistoryTransaction();
    },
    [beginPoseHistoryTransaction, commitHistoryTransaction],
  );
  const handlePropertiesManipulatorDraggingChange = useCallback(
    (isDragging: boolean) => {
      if (isDragging) {
        beginBonePropertiesHistoryTransaction(
          propertiesToolMode === 'limits'
            ? 'Edit rotation limits'
            : propertiesToolMode === 'angle-bias'
              ? 'Edit preferred angle bias'
              : 'Edit bone offsets',
        );
        return;
      }
      commitHistoryTransaction();
    },
    [
      beginBonePropertiesHistoryTransaction,
      commitHistoryTransaction,
      propertiesToolMode,
    ],
  );
  const handleBoneAssignmentsBoneSelect = useCallback(
    (index: number | null) => {
      store.selectBone(index);
    },
    [store],
  );
  const handleViewerBoneSelect = useCallback(
    (index: number | null) => {
      if (index === null) {
        store.clearSelection();
        if (isHistoryActive) {
          setDetailPanelMode('inspector');
          if (usesCombinedLayout) {
            const nextTab: CombinedPanelTab = 'detail';
            setIsCombinedPanelOpen(true);
            setActiveCombinedTab(nextTab);
            setCombinedPanelWidth(getCombinedMinWidthForTab(nextTab));
          } else {
            setIsInspectorOpen(true);
          }
          if (viewerToolMode !== 'select') {
            store.setViewerToolMode('select');
          }
        }
      } else {
        store.selectBone(index);
      }
      if (
        index === null &&
        isBoneAssignmentsActive &&
        viewerToolMode === 'select'
      ) {
        setBoneAssignmentsClearSelectionToken((token) => token + 1);
      }
    },
    [
      getCombinedMinWidthForTab,
      isBoneAssignmentsActive,
      isHistoryActive,
      setActiveCombinedTab,
      setCombinedPanelWidth,
      setDetailPanelMode,
      setIsCombinedPanelOpen,
      setIsInspectorOpen,
      store,
      usesCombinedLayout,
      viewerToolMode,
    ],
  );
  const handleBoneAssignmentsModeChange = useCallback(
    (mode: BoneAssignmentMode) => {
      setBoneAssignmentsMode(mode);
    },
    [],
  );
  const runAssignmentWithExcludedPrompt = useCallback(
    (
      assignment: PendingExcludedAssignment,
      excludedBoneRefs: readonly string[],
      onAssign: () => void,
    ) => {
      if (excludedBoneRefs.length === 0) {
        onAssign();
        return;
      }
      setPendingExcludedAssignment({
        ...assignment,
        excludedBoneRefs: [...excludedBoneRefs],
      });
    },
    [],
  );
  const handleAssignRole = useCallback(
    (boneName: string, nextRole: string | null) => {
      if (nextRole === null) {
        recordLegModeAwareMappingHistoryAction('Clear role assignment', () =>
          store.setBoneRoleOverride(boneName, null),
        );
        return;
      }
      const excludedBoneRefs = effectiveExcludedBoneRefSet.has(boneName)
        ? [boneName]
        : [];
      runAssignmentWithExcludedPrompt(
        {
          kind: 'role',
          excludedBoneRefs,
          boneRef: boneName,
          nextRole,
        },
        excludedBoneRefs,
        () =>
          recordLegModeAwareMappingHistoryAction('Assign bone role', () =>
            store.setBoneRoleOverride(boneName, nextRole),
          ),
      );
    },
    [
      effectiveExcludedBoneRefSet,
      recordLegModeAwareMappingHistoryAction,
      runAssignmentWithExcludedPrompt,
      store,
    ],
  );
  const handleSetChainFromStartEnd = useCallback(
    (chainName: string, startBoneRef: string, endBoneRef: string) => {
      const chainBoneRefs = buildShortestBonePathRefs(
        skeleton,
        startBoneRef,
        endBoneRef,
      ) ?? [startBoneRef, endBoneRef];
      const excludedBoneRefs = [...new Set(chainBoneRefs)].filter((ref) =>
        effectiveExcludedBoneRefSet.has(ref),
      );
      runAssignmentWithExcludedPrompt(
        {
          kind: 'chainPath',
          excludedBoneRefs,
          chainName,
          startBoneRef,
          endBoneRef,
        },
        excludedBoneRefs,
        () =>
          recordLegModeAwareMappingHistoryAction('Set chain path', () =>
            store.setChainFromStartEnd(chainName, startBoneRef, endBoneRef),
          ),
      );
    },
    [
      effectiveExcludedBoneRefSet,
      recordLegModeAwareMappingHistoryAction,
      runAssignmentWithExcludedPrompt,
      skeleton,
      store,
    ],
  );
  const handleAddBoneToChain = useCallback(
    (chainName: string, boneRef: string) => {
      const excludedBoneRefs = effectiveExcludedBoneRefSet.has(boneRef)
        ? [boneRef]
        : [];
      runAssignmentWithExcludedPrompt(
        {
          kind: 'chainAdd',
          excludedBoneRefs,
          chainName,
          boneRef,
        },
        excludedBoneRefs,
        () =>
          recordLegModeAwareMappingHistoryAction('Add bone to chain', () =>
            store.addBoneToChain(chainName, boneRef),
          ),
      );
    },
    [
      effectiveExcludedBoneRefSet,
      recordLegModeAwareMappingHistoryAction,
      runAssignmentWithExcludedPrompt,
      store,
    ],
  );
  const handleConfirmExcludedAssignment = useCallback(() => {
    if (!pendingExcludedAssignment) return;
    const historyLabel =
      pendingExcludedAssignment.kind === 'role'
        ? 'Include bone and assign role'
        : pendingExcludedAssignment.kind === 'chainAdd'
          ? 'Include bone and add to chain'
          : 'Include bones and set chain path';
    recordLegModeAwareMappingHistoryAction(historyLabel, () => {
      const refsToInclude = pendingExcludedAssignment.excludedBoneRefs.filter(
        (ref) => effectiveExcludedBoneRefSet.has(ref),
      );
      for (const boneRef of refsToInclude) {
        store.toggleBoneExcluded(boneRef);
      }
      switch (pendingExcludedAssignment.kind) {
        case 'role':
          store.setBoneRoleOverride(
            pendingExcludedAssignment.boneRef,
            pendingExcludedAssignment.nextRole,
          );
          break;
        case 'chainAdd':
          store.addBoneToChain(
            pendingExcludedAssignment.chainName,
            pendingExcludedAssignment.boneRef,
          );
          break;
        case 'chainPath':
          store.setChainFromStartEnd(
            pendingExcludedAssignment.chainName,
            pendingExcludedAssignment.startBoneRef,
            pendingExcludedAssignment.endBoneRef,
          );
          break;
      }
      setPendingExcludedAssignment(null);
    });
  }, [
    effectiveExcludedBoneRefSet,
    pendingExcludedAssignment,
    recordLegModeAwareMappingHistoryAction,
    store,
  ]);
  const excludedAssignmentPromptTitle = useMemo(() => {
    if (!pendingExcludedAssignment) return '';
    return pendingExcludedAssignment.excludedBoneRefs.length > 1
      ? 'Excluded bone assignment'
      : 'Excluded bone assignment';
  }, [pendingExcludedAssignment]);
  const excludedAssignmentPromptDescription = useMemo(() => {
    if (!pendingExcludedAssignment) return null;
    const boneRefsLabel = pendingExcludedAssignment.excludedBoneRefs
      .map((ref) => `"${ref}"`)
      .join(', ');
    const actionLabel =
      pendingExcludedAssignment.kind === 'role'
        ? `role "${pendingExcludedAssignment.nextRole}"`
        : `chain "${pendingExcludedAssignment.chainName}"`;
    return (
      <Text
        variant="studio/body/medium/sm"
        className="text-gray-60 text-[16px]/[20px]"
      >
        Include {boneRefsLabel} before assigning to{' '}
        <Text as="span" variant="studio/body/bold/sm">
          {actionLabel}
        </Text>
        .
      </Text>
    );
  }, [pendingExcludedAssignment]);
  const handleBoneAssignmentsViewerPickModeChange = useCallback(
    (mode: BoneAssignmentsViewerPickMode | null) => {
      viewerPickModeOrderRef.current += 1;
      setBoneAssignmentsViewerPickModeState({
        mode,
        order: viewerPickModeOrderRef.current,
      });
    },
    [],
  );
  const handleOutlinerViewerPickModeChange = useCallback(
    (mode: BoneAssignmentsViewerPickMode | null) => {
      viewerPickModeOrderRef.current += 1;
      setOutlinerViewerPickModeState({
        mode,
        order: viewerPickModeOrderRef.current,
      });
    },
    [],
  );
  useCartographerHotkeys({
    onFocusHotkey: handleFocusHotkey,
    enabled: activeFocusSurface !== null,
    onUndo: handleUndo,
    onRedo: handleRedo,
    historyEnabled: hasGltfScene || hasEverLoadedScene.current,
  });
  useEffect(() => {
    const previousVisibility = viewerVisibilityBeforeBoneAssignmentsRef.current;
    if (!activeBoneAssignmentsMode) {
      if (!previousVisibility) return;
      viewerVisibilityBeforeBoneAssignmentsRef.current = null;
      store.setVisibilityValues(previousVisibility);
      return;
    }

    if (previousVisibility === null) {
      viewerVisibilityBeforeBoneAssignmentsRef.current = {
        showRoles: visibility.showRoles,
        showChains: visibility.showChains,
        showUnassignedBones: visibility.showUnassignedBones,
      };
    }

    store.setVisibilityValues(
      activeBoneAssignmentsMode === 'roles'
        ? { showRoles: true, showChains: false, showUnassignedBones: true }
        : { showRoles: false, showChains: true, showUnassignedBones: true },
    );
  }, [
    activeBoneAssignmentsMode,
    store,
    visibility.showChains,
    visibility.showUnassignedBones,
    visibility.showRoles,
  ]);
  useEffect(
    () => () => {
      const previousVisibility =
        viewerVisibilityBeforeBoneAssignmentsRef.current;
      if (!previousVisibility) return;
      viewerVisibilityBeforeBoneAssignmentsRef.current = null;
      store.setVisibilityValues(previousVisibility);
    },
    [store],
  );

  const handleViewerVisibilityToggle = useCallback(
    (key: string) => {
      if (!(key in visibility)) return;
      const visibilityKey = key as keyof ViewerVisibility;
      if (
        activeBoneAssignmentsMode &&
        (visibilityKey === 'showRoles' ||
          visibilityKey === 'showChains' ||
          visibilityKey === 'showUnassignedBones')
      ) {
        return;
      }
      store.toggleVisibility(visibilityKey);
    },
    [activeBoneAssignmentsMode, store, visibility],
  );
  const outlinerFooterSettingsControl = (
    <OutlinerSettingsControl {...settingsControlProps} />
  );
  const showBoneAssignmentsDetailPanel = useCallback(() => {
    setDetailPanelMode('boneAssignments');
    setBoneAssignmentsMode('roles');
    setBoneAssignmentsTitle('Bone Assignments');
    setBoneAssignmentsViewerRoleTag(null);
    setBoneAssignmentsViewerFocus(null);
    setBoneAssignmentsHoveredChain(null);

    if (viewerToolMode !== 'select') {
      store.setViewerToolMode('select');
    }

    if (usesCombinedLayout) {
      const nextTab: CombinedPanelTab = 'detail';
      setIsCombinedPanelOpen(true);
      setActiveCombinedTab(nextTab);
      setCombinedPanelWidth(getCombinedMinWidthForTab(nextTab));
      return;
    }

    setIsInspectorOpen(true);
  }, [
    getCombinedMinWidthForTab,
    setActiveCombinedTab,
    setCombinedPanelWidth,
    setIsCombinedPanelOpen,
    setIsInspectorOpen,
    store,
    usesCombinedLayout,
    viewerToolMode,
  ]);
  const hideBoneAssignmentsDetailPanel = useCallback(() => {
    setDetailPanelMode('inspector');
    setBoneAssignmentsMode('roles');
    setBoneAssignmentsViewerRoleTag(null);
    setBoneAssignmentsViewerFocus(null);
    setBoneAssignmentsHoveredChain(null);

    if (usesCombinedLayout) {
      const nextTab: CombinedPanelTab = 'detail';
      setIsCombinedPanelOpen(true);
      setActiveCombinedTab(nextTab);
      setCombinedPanelWidth(getCombinedMinWidthForTab(nextTab));
      return;
    }

    setIsInspectorOpen(true);
  }, [
    getCombinedMinWidthForTab,
    setActiveCombinedTab,
    setCombinedPanelWidth,
    setIsCombinedPanelOpen,
    setIsInspectorOpen,
    usesCombinedLayout,
  ]);
  const showHistoryDetailPanel = useCallback(() => {
    setDetailPanelMode('history');

    if (usesCombinedLayout) {
      const nextTab: CombinedPanelTab = 'detail';
      setIsCombinedPanelOpen(true);
      setActiveCombinedTab(nextTab);
      setCombinedPanelWidth(getCombinedMinWidthForTab(nextTab));
      return;
    }

    setIsInspectorOpen(true);
  }, [
    getCombinedMinWidthForTab,
    setActiveCombinedTab,
    setCombinedPanelWidth,
    setIsCombinedPanelOpen,
    setIsInspectorOpen,
    usesCombinedLayout,
  ]);
  const hideHistoryDetailPanel = useCallback(() => {
    setDetailPanelMode('inspector');

    if (usesCombinedLayout) {
      const nextTab: CombinedPanelTab = 'detail';
      setIsCombinedPanelOpen(true);
      setActiveCombinedTab(nextTab);
      setCombinedPanelWidth(getCombinedMinWidthForTab(nextTab));
      return;
    }

    setIsInspectorOpen(true);
  }, [
    getCombinedMinWidthForTab,
    setActiveCombinedTab,
    setCombinedPanelWidth,
    setIsCombinedPanelOpen,
    setIsInspectorOpen,
    usesCombinedLayout,
  ]);
  const handleHistoryToggle = useCallback(() => {
    if (!hasGltfScene && historyItems.length === 0) return;

    if (isHistoryActive) {
      hideHistoryDetailPanel();
      return;
    }

    showHistoryDetailPanel();
  }, [
    hasGltfScene,
    hideHistoryDetailPanel,
    historyItems.length,
    isHistoryActive,
    showHistoryDetailPanel,
  ]);
  const handleBoneAssignmentsToggle = useCallback(() => {
    if (!hasRenderableSkeleton) return;

    if (isBoneAssignmentsActive) {
      hideBoneAssignmentsDetailPanel();
      return;
    }

    showBoneAssignmentsDetailPanel();
  }, [
    hasRenderableSkeleton,
    hideBoneAssignmentsDetailPanel,
    isBoneAssignmentsActive,
    showBoneAssignmentsDetailPanel,
  ]);
  const runMappingRecognition = useCallback(() => {
    recordLegModeAwareMappingHistoryAction('Recognize mapping', () => {
      store.runMappingRecognition();
    });
  }, [recordLegModeAwareMappingHistoryAction, store]);
  const clearMappingRecognition = useCallback(() => {
    recordMappingHistoryAction('Clear mapping', () => {
      store.clearMappingRecognition();
    });
  }, [recordMappingHistoryAction, store]);
  const { mappingSection, resetMappingSection } = useCartographerMappingSection(
    {
      hasRenderableSkeleton,
      mappingHasAssignments,
      isBoneAssignmentsActive,
      boneAssignmentsMode,
      viewerToolMode,
      showBoneAssignmentsDetailPanel,
      hideBoneAssignmentsDetailPanel,
      onBoneAssignmentsToggle: handleBoneAssignmentsToggle,
      onBoneAssignmentsModeChange: handleBoneAssignmentsModeChange,
      runMappingRecognition,
      clearMappingRecognition,
    },
  );
  const handleDockViewerToolModeChange = useCallback(
    (mode: ViewerToolMode) => {
      if (isHistoryActive) {
        hideHistoryDetailPanel();
      }
      store.setViewerToolMode(mode);
    },
    [hideHistoryDetailPanel, isHistoryActive, store],
  );
  const handleDockRotateSpaceModeChange = useCallback(
    (mode: RotateSpaceMode) => {
      if (isHistoryActive) {
        hideHistoryDetailPanel();
      }
      store.setRotateSpaceMode(mode);
    },
    [hideHistoryDetailPanel, isHistoryActive, store],
  );
  const handleDockPropertiesToolModeChange = useCallback(
    (mode: PropertiesToolMode) => {
      if (isHistoryActive) {
        hideHistoryDetailPanel();
      }
      store.setPropertiesToolMode(mode);
    },
    [hideHistoryDetailPanel, isHistoryActive, store],
  );
  const dockMappingSection = useMemo(
    () => ({
      ...mappingSection,
      onMappingOptionsToggle: () => {
        if (isHistoryActive) {
          hideHistoryDetailPanel();
        }
        mappingSection.onMappingOptionsToggle();
      },
    }),
    [hideHistoryDetailPanel, isHistoryActive, mappingSection],
  );
  const resetCharacterOpenModes = useCallback(() => {
    resetMappingSection();
    setDetailPanelMode('inspector');
    setBoneAssignmentsMode('roles');
    setBoneAssignmentsTitle('Bone Assignments');
    setBoneAssignmentsViewerRoleTag(null);
    setBoneAssignmentsViewerFocus(null);
    setBoneAssignmentsHoveredChain(null);
    setBoneAssignmentsViewerPickModeState((state) =>
      state.mode === null ? state : { mode: null, order: state.order },
    );
    setOutlinerViewerPickModeState((state) =>
      state.mode === null ? state : { mode: null, order: state.order },
    );
    if (viewerToolMode !== 'select') {
      store.setViewerToolMode('select');
    }
  }, [resetMappingSection, store, viewerToolMode]);

  const loadLocalModelFile = useCallback(
    (chosen: File) => {
      pushCartographerDebugEvent('character.loadLocalFile.start', {
        name: chosen.name,
        size: chosen.size,
      });
      resetMotionMiniState();
      resetCharacterOpenModes();
      setLoadedCharacterID(null);
      characterConfigImportStrictRef.current = false;
      pendingMappingImportJsonRef.current = null;
      setPendingLoadedCharacterBaseline(null);
      setLastSavedMarker(null);
      store.setFile(chosen);
    },
    [
      resetCharacterOpenModes,
      resetMotionMiniState,
      setPendingLoadedCharacterBaseline,
      store,
    ],
  );

  const handleImportMapping = useCallback(() => {
    mappingInputRef.current?.click();
  }, []);

  const handleCopyCharacterId = useCallback(async () => {
    if (!loadedCharacterID) return;
    try {
      await navigator.clipboard.writeText(loadedCharacterID);
      noticesStore.success(
        `${loadedCharacterID} has been copied to the clipboard`,
      );
    } catch {
      noticesStore.error('Could not copy to clipboard');
    }
  }, [loadedCharacterID]);

  const handleOpenInMotionEditor = useCallback(async () => {
    if (!apiKey) {
      noticesStore.error('API key not available. Please try again.');
      return;
    }
    if (!selectedMotionId) {
      return;
    }
    if (!loadedCharacterID) {
      noticesStore.error(
        'Save this character to your library before opening it in Motion Editor.',
      );
      return;
    }

    const characterName =
      fileName?.replace(/\.[a-zA-Z0-9]+$/, '').trim() || 'Character';
    const motionName = selectedMotionLabel?.trim() || selectedMotionId;
    const sceneName = `${motionName} (${characterName})`;

    setOpeningMotionEditor(true);
    try {
      const sceneID = await createMotionEditorSceneIdFromMotion({
        apiKey,
        motionID: selectedMotionId,
        sceneName,
        characterID: loadedCharacterID,
        outputNumber: selectedBodyIndex ?? 0,
      });
      noticesStore.success('Scene created');
      navigate(`${MOTION_EDITOR_PATH}/${encodeURIComponent(sceneID)}`);
    } catch (error) {
      noticesStore.error(
        error instanceof Error ? error.message : 'Failed to create scene.',
      );
    } finally {
      setOpeningMotionEditor(false);
    }
  }, [
    apiKey,
    fileName,
    loadedCharacterID,
    navigate,
    selectedBodyIndex,
    selectedMotionId,
    selectedMotionLabel,
  ]);

  const handleMappingFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const chosen = e.target.files?.[0];
      if (!chosen) {
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jsonStr = reader.result as string;
          if (store.getSkeleton()) {
            recordMappingHistoryAction('Import mapping', () => {
              store.applyImportedMappingFromJson(jsonStr);
            });
          } else {
            pendingMappingImportJsonRef.current = jsonStr;
          }
        } catch (err) {
          store.setError(
            err instanceof Error ? err.message : 'Failed to import mapping',
          );
        }
      };
      reader.readAsText(chosen);
      e.target.value = '';
    },
    [recordMappingHistoryAction, store],
  );

  const handleCloseCharacterDrawer = useCallback(() => {
    setIsCharacterDrawerOpen(false);
  }, []);

  const handleToggleCharacterDrawer = useCallback(() => {
    setIsCharacterDrawerOpen((isOpen) => !isOpen);
  }, []);

  const clearPendingCharacterLoadAction = useCallback(() => {
    pendingCharacterLoadActionRef.current = null;
    setPendingCharacterLoadTargetLabel('this character');
  }, []);

  const requestCharacterLoad = useCallback(
    (startLoad: PendingCharacterLoadAction, targetLabel = 'this character') => {
      if (!isCharacterDirty || !hasGltfScene) {
        void Promise.resolve(startLoad());
        return;
      }
      pendingCharacterLoadActionRef.current = startLoad;
      setPendingCharacterLoadTargetLabel(targetLabel);
      setLoadCharacterUnsavedModalOpen(true);
    },
    [hasGltfScene, isCharacterDirty],
  );

  const beginCharacterSwitchFromDrawer = useCallback(
    (startLoad: () => Promise<boolean> | boolean) => {
      pushCartographerDebugEvent('character.switch.begin', {
        source: 'drawer',
      });
      setCharacterSwitchPhase('priming');
      setHasObservedSwitchLoading(false);
      window.requestAnimationFrame(() => {
        pushCartographerDebugEvent('character.switch.phase', {
          phase: 'loading',
        });
        const keepDrawerOpenForUpload =
          characterDrawerUploadPanelOpenRef.current ||
          characterDrawerUploadPreparingRef.current;
        if (!keepDrawerOpenForUpload) {
          handleCloseCharacterDrawer();
        }
        setCharacterSwitchPhase('loading');
        void Promise.resolve(startLoad())
          .then((startedLoading) => {
            if (startedLoading !== false) {
              pushCartographerDebugEvent('character.switch.startLoad.accepted');
              return;
            }
            pushCartographerDebugEvent('character.switch.startLoad.rejected');
            setHasObservedSwitchLoading(false);
            setCharacterSwitchPhase('idle');
          })
          .catch((error: unknown) => {
            pushCartographerDebugEvent('character.switch.startLoad.error', {
              error: stringifyDebugReason(error),
            });
            setHasObservedSwitchLoading(false);
            setCharacterSwitchPhase('idle');
          });
      });
    },
    [handleCloseCharacterDrawer],
  );

  const refreshCharacters = useCallback(async () => {
    const fetchId = ++charactersFetchIdRef.current;
    if (!apiKey) {
      setCharactersList([]);
      setCharactersLoading(false);
      setCharactersError(null);
      charactersCacheApiKeyRef.current = null;
      charactersLoadedOnceRef.current = false;
      return;
    }

    const sessionCachedCharacters = readCharactersSessionCache(
      apiKey,
      organizationServiceId,
    );
    if (
      sessionCachedCharacters &&
      (!charactersLoadedOnceRef.current ||
        charactersCacheApiKeyRef.current !== apiKey)
    ) {
      setCharactersList(sessionCachedCharacters);
      setCharactersError(null);
      charactersCacheApiKeyRef.current = apiKey;
      charactersLoadedOnceRef.current = true;
    }

    const hasWarmCache =
      charactersLoadedOnceRef.current &&
      charactersCacheApiKeyRef.current === apiKey;
    if (hasWarmCache) {
      // Keep thumbnails visible while we revalidate in the background.
      setCharactersLoading(false);
    } else {
      setCharactersError(null);
      setCharactersList([]);
      setCharactersLoading(true);
    }

    try {
      const { data, error } = await getExternalCharacters(apiKey, {
        limit: 50,
        sortAscending: false,
      });
      if (fetchId !== charactersFetchIdRef.current) return;
      if (error) {
        if (!hasWarmCache) {
          setCharactersError(error.message);
          setCharactersList([]);
        }
      } else {
        const nextCharacters = data?.characters ?? [];
        setCharactersList(nextCharacters);
        setCharactersError(null);
        charactersCacheApiKeyRef.current = apiKey;
        charactersLoadedOnceRef.current = true;
        writeCharactersSessionCache(
          apiKey,
          organizationServiceId,
          nextCharacters,
        );
      }
    } catch (err) {
      if (fetchId !== charactersFetchIdRef.current) return;
      if (!hasWarmCache) {
        setCharactersError(
          err instanceof Error ? err.message : 'Failed to load characters',
        );
        setCharactersList([]);
      }
    } finally {
      if (fetchId === charactersFetchIdRef.current) {
        setCharactersLoading(false);
      }
    }
  }, [apiKey, organizationServiceId]);

  useEffect(() => {
    if (!showWelcomeScreen && !showCharacterDrawer) return;
    refreshCharacters();
  }, [refreshCharacters, showCharacterDrawer, showWelcomeScreen]);

  useEffect(() => {
    if (showEditorChrome) return;
    if (!isCharacterDrawerOpen) return;
    setIsCharacterDrawerOpen(false);
  }, [isCharacterDrawerOpen, showEditorChrome]);

  useEffect(() => {
    if (panelsBlocked) {
      setSidePanelsVisible(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSidePanelsVisible(true);
    }, SIDE_PANELS_FADE_IN_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [panelsBlocked]);

  useEffect(() => {
    if (characterSwitchPhase !== 'loading') return;

    if (loading) {
      pushCartographerDebugEvent('character.switch.waiting.loading', {
        hasObservedSwitchLoading,
      });
      if (!hasObservedSwitchLoading) {
        setHasObservedSwitchLoading(true);
      }
      return;
    }

    if (!hasObservedSwitchLoading) {
      pushCartographerDebugEvent('character.switch.waiting.noObservedLoading');
      return;
    }
    pushCartographerDebugEvent('character.switch.done.idle');
    setHasObservedSwitchLoading(false);
    setCharacterSwitchPhase('idle');
  }, [characterSwitchPhase, hasObservedSwitchLoading, loading]);

  const handleSelectCharacter = useCallback(
    async (character: ExternalCharacterListItem) => {
      const url = character.characterFileURL;
      if (!url) return false;
      pushCartographerDebugEvent('character.select.start', {
        characterID: character.jobID,
        characterName: character.characterName,
        hasConfig: Boolean(character.configURL),
      });
      resetMotionMiniState();
      resetCharacterOpenModes();
      setLoadingCharacterId(character.jobID);
      try {
        const configUrl = character.configURL ?? null;
        const meshPromise = fetch(url).then((res) => {
          if (!res.ok) throw new Error(res.statusText);
          return res.blob();
        });
        const configPromise = configUrl
          ? fetch(configUrl).then((res) => {
              if (!res.ok) {
                throw new Error(`Config HTTP ${res.status} ${res.statusText}`);
              }
              return res.text();
            })
          : Promise.resolve<string | null>(null);

        const [blob, configJson] = await Promise.all([
          meshPromise,
          configPromise,
        ]);

        if (configUrl) {
          if (configJson == null || !configJson.trim()) {
            throw new Error('Character config is empty');
          }
        }

        const file = new File(
          [blob],
          `${character.characterName}.${character.extension}`.replace(
            /[^a-zA-Z0-9._-]/g,
            '_',
          ),
          { type: blob.type },
        );

        characterConfigImportStrictRef.current = Boolean(configUrl);
        pendingMappingImportJsonRef.current = configJson;
        setPendingLoadedCharacterBaseline(character.jobID);
        store.setFile(file);
        setLoadedCharacterID(character.jobID);
        setLoadCharacterModalOpen(false);
        pushCartographerDebugEvent('character.select.success', {
          characterID: character.jobID,
          fileName: file.name,
          fileSize: file.size,
        });
        return true;
      } catch (err) {
        pushCartographerDebugEvent('character.select.error', {
          characterID: character.jobID,
          error: stringifyDebugReason(err),
        });
        setPendingLoadedCharacterBaseline(null);
        characterConfigImportStrictRef.current = false;
        pendingMappingImportJsonRef.current = null;
        store.setError(
          err instanceof Error ? err.message : 'Failed to load character',
        );
        return false;
      } finally {
        setLoadingCharacterId(null);
      }
    },
    [
      resetCharacterOpenModes,
      resetMotionMiniState,
      setPendingLoadedCharacterBaseline,
      store,
    ],
  );

  const handleSelectCharacterWithPrompt = useCallback(
    (character: ExternalCharacterListItem) => {
      requestCharacterLoad(
        () => handleSelectCharacter(character),
        character.characterName || 'this character',
      );
    },
    [handleSelectCharacter, requestCharacterLoad],
  );

  const handleSelectCharacterFromDrawer = useCallback(
    (character: ExternalCharacterListItem) => {
      requestCharacterLoad(
        () =>
          beginCharacterSwitchFromDrawer(() =>
            handleSelectCharacter(character),
          ),
        character.characterName || 'this character',
      );
    },
    [
      beginCharacterSwitchFromDrawer,
      handleSelectCharacter,
      requestCharacterLoad,
    ],
  );

  const handleModelFileSelectedFromDrawer = useCallback(
    (file: File) => {
      requestCharacterLoad(
        () =>
          beginCharacterSwitchFromDrawer(() => {
            loadLocalModelFile(file);
            return true;
          }),
        file.name || 'your uploaded model',
      );
    },
    [beginCharacterSwitchFromDrawer, loadLocalModelFile, requestCharacterLoad],
  );

  const handleModelFileSelectedWithPrompt = useCallback(
    (file: File) => {
      requestCharacterLoad(
        () => loadLocalModelFile(file),
        file.name || 'your uploaded model',
      );
    },
    [loadLocalModelFile, requestCharacterLoad],
  );

  const handleDeleteLibraryCharacter = useCallback(
    async (character: ExternalCharacterListItem): Promise<boolean> => {
      if (!apiKey) return false;

      store.setError(null);
      try {
        const { error } = await externalDeleteCharacter(
          apiKey,
          character.jobID,
        );
        if (error) {
          store.setError(error.message ?? 'Failed to delete character');
          return false;
        }

        setCharactersList((prev) =>
          prev.filter((entry) => entry.jobID !== character.jobID),
        );
        if (loadedCharacterID === character.jobID) {
          setLoadedCharacterID(null);
        }
        return true;
      } catch (err) {
        store.setError(
          err instanceof Error ? err.message : 'Failed to delete character',
        );
        return false;
      }
    },
    [apiKey, loadedCharacterID, store],
  );

  const handleRenameLibraryCharacter = useCallback(
    async (
      character: ExternalCharacterListItem,
      nextCharacterName: string,
    ): Promise<boolean> => {
      if (!apiKey) return false;

      const trimmedName = nextCharacterName.trim();
      if (!trimmedName) {
        store.setError('Name cannot be empty');
        return false;
      }
      if (trimmedName === character.characterName) {
        return true;
      }

      const { data, error } = await patchUpdateCharacter(
        apiKey,
        character.jobID,
        {
          characterName: trimmedName,
        },
      );
      if (error || !data) {
        store.setError(error?.message ?? 'Failed to rename character');
        return false;
      }

      const resolvedCharacterName = data.characterName?.trim() || trimmedName;
      const resolvedDisplayName = `${resolvedCharacterName}.${character.extension}`;
      setCharactersList((prev) =>
        prev.map((entry) =>
          entry.jobID === character.jobID
            ? { ...entry, characterName: resolvedCharacterName }
            : entry,
        ),
      );

      if (loadedCharacterID === character.jobID) {
        store.setDisplayFileName(resolvedDisplayName);
        setLastSavedMarker((prevMarker) =>
          prevMarker?.characterID === character.jobID
            ? { ...prevMarker, fileName: resolvedDisplayName }
            : prevMarker,
        );
      }
      return true;
    },
    [apiKey, loadedCharacterID, store],
  );

  const handleCharacterFileRename = useCallback(
    async (nextDisplayFileName: string): Promise<boolean> => {
      const trimmedFull = nextDisplayFileName.trim();
      if (!trimmedFull) {
        store.setError('Name cannot be empty');
        return false;
      }

      const currentFile = store.getFile();
      const ext =
        currentFile?.name.match(/\.([^.]+)$/i)?.[1]?.toLowerCase() ??
        fileName?.match(/\.([^.]+)$/i)?.[1]?.toLowerCase() ??
        'glb';

      let apiCharacterName = trimmedFull;
      const lastSeg = trimmedFull.match(/\.([^.]+)$/i);
      if (lastSeg && lastSeg[1].toLowerCase() === ext) {
        apiCharacterName = trimmedFull
          .slice(0, trimmedFull.length - lastSeg[0].length)
          .trim();
      }
      if (!apiCharacterName) {
        store.setError('Name cannot be empty');
        return false;
      }

      store.setError(null);
      const nextDisplayName = `${apiCharacterName}.${ext}`;
      if (nextDisplayName === fileName) {
        return true;
      }
      store.setDisplayFileName(nextDisplayName);
      return true;
    },
    [store, fileName],
  );

  const snapshotSaveMarker = useCallback(
    (
      characterID: string | null = loadedCharacterID,
    ): CharacterSaveMarker | null => {
      if (!store.getFile()) return null;
      return {
        fileVersion: store.state.fileVersion,
        fileName: store.state.fileName,
        mappingVersion: store.state.mappingVersion,
        characterID,
      };
    },
    [loadedCharacterID, store],
  );

  const handleSave = useCallback(
    async (options: CharacterSaveOptions = {}) => {
      const file = store.getFile();
      if (!file) {
        store.setError('A model file is required to save your character.');
        return false;
      }
      if (!apiKey) {
        if (authStatus !== 'user') {
          requestAppletSignInModal();
          return false;
        }
        if (apiKeyState.status !== 'ready') {
          return false;
        }
        requestAppletUpgradeModal();
        return false;
      }
      const mapping = store.getMappingResult();
      const skeleton = store.getSkeleton();
      const saveMode: CharacterSaveMode =
        options.mode === 'new' || !loadedCharacterID ? 'new' : 'overwrite';
      const displayName = fileName ?? file.name;
      const characterName =
        displayName.replace(/\.[a-zA-Z0-9]+$/, '').trim() ||
        file.name.replace(/\.[a-zA-Z0-9]+$/, '') ||
        'character';
      const saveStartMarker = snapshotSaveMarker(loadedCharacterID);
      let nextCharacterID = saveMode === 'overwrite' ? loadedCharacterID : null;
      let saveSucceeded = false;

      const uploadThumbnail = async ({
        thumbnailUploadURL,
        uploadErrorPrefix,
        captureErrorMessage,
      }: {
        thumbnailUploadURL: string;
        uploadErrorPrefix: string;
        captureErrorMessage: string;
      }) => {
        if (!screenshotRef.current) return;
        try {
          const thumbnailBlob = await screenshotRef.current();
          void logThumbnailCaptureMetrics(saveMode, thumbnailBlob);
          const putResThumb = await fetch(
            thumbnailUploadURL,
            buildPresignedPutOptions(
              thumbnailBlob,
              CHARACTER_THUMBNAIL_CONTENT_TYPE,
            ),
          );
          if (!putResThumb.ok) {
            store.setError(`${uploadErrorPrefix}: ${putResThumb.status}`);
          }
        } catch {
          store.setError(captureErrorMessage);
        }
      };

      setSaveInProgressMode(saveMode);
      setSaveInProgress(true);
      store.setError(null);
      try {
        if (saveMode === 'overwrite' && loadedCharacterID) {
          const { data, error: apiError } = await patchUpdateCharacter(
            apiKey,
            loadedCharacterID,
            {
              characterName,
              requestConfigUploadURL: true,
            },
          );
          if (apiError || !data) {
            store.setError(
              apiError?.message ?? 'Failed to prepare character overwrite save',
            );
            return false;
          }
          if (mapping && data.configUploadURL) {
            const configJson = exportMappingJson(mapping, skeleton);
            const configBlob = new Blob([configJson], {
              type: 'application/json',
            });
            const putResConfig = await fetch(
              data.configUploadURL,
              buildPresignedPutOptions(configBlob, 'application/json'),
            );
            if (!putResConfig.ok) {
              store.setError(
                `Failed to upload updated config: ${putResConfig.status} ${putResConfig.statusText}`,
              );
              return false;
            }
          }
          if (data.thumbnailUploadURL && screenshotRef.current) {
            await uploadThumbnail({
              thumbnailUploadURL: data.thumbnailUploadURL,
              uploadErrorPrefix: 'Changes saved; thumbnail upload failed',
              captureErrorMessage: 'Changes saved; thumbnail capture failed.',
            });
          }
          saveSucceeded = true;
        } else {
          const match = file.name.match(/\.([a-zA-Z0-9]+)$/);
          const fileExtension = (match?.[1] ?? 'fbx').toLowerCase();
          const { data, error: apiError } = await postCreateCharacterUpload(
            apiKey,
            {
              fileExtension,
              characterName,
              characterDescription: 'Saved from Cartographer',
              createdByUser: userStore.state.userInfo?.userID,
              thumbnailExtension: CHARACTER_THUMBNAIL_EXTENSION,
            },
          );
          if (apiError || !data) {
            store.setError(
              apiError?.message ?? 'Failed to start a new character save',
            );
            return false;
          }
          const putResFile = await fetch(
            data.characterFileUploadURL,
            buildPresignedPutOptions(
              file,
              file.type || 'application/octet-stream',
            ),
          );
          if (!putResFile.ok) {
            store.setError(
              `Failed to upload new model: ${putResFile.status} ${putResFile.statusText}`,
            );
            return false;
          }
          if (mapping && data.configUploadURL) {
            const configJson = exportMappingJson(mapping, skeleton);
            const configBlob = new Blob([configJson], {
              type: 'application/json',
            });
            const putResConfig = await fetch(
              data.configUploadURL,
              buildPresignedPutOptions(configBlob, 'application/json'),
            );
            if (!putResConfig.ok) {
              store.setError(
                `Failed to upload new config: ${putResConfig.status} ${putResConfig.statusText}`,
              );
              return false;
            }
          }
          if (data.thumbnailUploadURL && screenshotRef.current) {
            await uploadThumbnail({
              thumbnailUploadURL: data.thumbnailUploadURL,
              uploadErrorPrefix: 'Character created; thumbnail upload failed',
              captureErrorMessage:
                'Character created; thumbnail capture failed.',
            });
          }
          nextCharacterID = data.characterID;
          setLoadedCharacterID(data.characterID);
          saveSucceeded = true;
        }
      } catch (err) {
        store.setError(
          err instanceof Error ? err.message : 'Failed to save character',
        );
        return false;
      } finally {
        if (saveSucceeded) {
          void refreshCharacters();
          if (!store.state.error) {
            noticesStore.success(
              saveMode === 'new' ? 'Character created.' : 'Changes saved.',
            );
          }
          const liveMarker = snapshotSaveMarker(nextCharacterID);
          if (
            saveStartMarker &&
            liveMarker &&
            liveMarker.fileVersion === saveStartMarker.fileVersion &&
            liveMarker.fileName === saveStartMarker.fileName &&
            liveMarker.mappingVersion === saveStartMarker.mappingVersion
          ) {
            setLastSavedMarker(liveMarker);
          }
        }
        setSaveInProgress(false);
        setSaveInProgressMode(null);
      }
      return saveSucceeded;
    },
    [
      store,
      apiKey,
      apiKeyState.status,
      authStatus,
      loadedCharacterID,
      fileName,
      refreshCharacters,
      snapshotSaveMarker,
    ],
  );
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);
  const handleSaveAsNew = useCallback(async () => {
    return handleSave({ mode: 'new' });
  }, [handleSave]);

  const handleContinueLoadingCharacterWithoutSaving = useCallback(() => {
    const pendingLoadAction = pendingCharacterLoadActionRef.current;
    setLoadCharacterUnsavedModalOpen(false);
    clearPendingCharacterLoadAction();
    if (!pendingLoadAction) return;
    void Promise.resolve(pendingLoadAction());
  }, [clearPendingCharacterLoadAction]);

  const handleSaveAndLoadCharacter = useCallback(async () => {
    const pendingLoadAction = pendingCharacterLoadActionRef.current;
    if (!pendingLoadAction) return;

    setLoadCharacterSaveInProgress(true);
    try {
      const didSave = await handleSave();
      if (!didSave) return;
      setLoadCharacterUnsavedModalOpen(false);
      clearPendingCharacterLoadAction();
      void Promise.resolve(pendingLoadAction());
    } finally {
      setLoadCharacterSaveInProgress(false);
    }
  }, [clearPendingCharacterLoadAction, handleSave]);

  useEffect(() => {
    if (!hasGltfScene || loading) return;

    const timeoutId = window.setTimeout(() => {
      store.captureHistoryBaseline(
        pendingLoadedCharacterBaselineID ? 'Loaded character' : 'Initial state',
      );

      if (!pendingLoadedCharacterBaselineID) {
        return;
      }

      // Capture baseline after load settles so auto-recognition/import mutations
      // are included and don't immediately appear as unsaved edits.
      const marker = snapshotSaveMarker(pendingLoadedCharacterBaselineID);
      if (marker) {
        setLastSavedMarker(marker);
        store.markCurrentHistoryEntrySaved();
      }
      console.log('[Cartographer][DirtyBaseline]', {
        baselineCharacterID: pendingLoadedCharacterBaselineID,
        marker,
        fileVersion: store.state.fileVersion,
        mappingVersion: store.state.mappingVersion,
      });
      setPendingLoadedCharacterBaseline(null);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    hasGltfScene,
    loading,
    pendingLoadedCharacterBaselineID,
    setPendingLoadedCharacterBaseline,
    snapshotSaveMarker,
    store,
  ]);

  useEffect(() => {
    const file = store.getFile();
    if (!file) {
      pushCartographerDebugEvent('model.load.skipped.no-file');
      return;
    }

    const loadGeneration = ++modelLoadGenerationRef.current;
    const loadStartedAt = performance.now();
    const getLoadElapsedMs = () =>
      Math.round(performance.now() - loadStartedAt);
    const getDurationMs = (startedAt: number) =>
      Number((performance.now() - startedAt).toFixed(2));
    pushCartographerDebugEvent('model.load.start', {
      loadGeneration,
      fileName: file.name,
      fileSize: file.size,
      extension: file.name.split('.').pop()?.toLowerCase() ?? null,
      loadedCharacterID,
    });

    store.setLoading(true);
    store.setError(null);
    store.setWarning(null);

    const isFBX = file.name.toLowerCase().endsWith('.fbx');
    const url = isFBX ? null : URL.createObjectURL(file);
    objectUrlRef.current = url;

    function stripDisplaySceneMorphTargets(
      clone: THREE.Group,
      reason: 'skeleton' | 'fallback',
    ) {
      const stripStartedAt = performance.now();
      const beforeStats = collectSceneAssetStats(clone);
      const opaqueBlendMaterialCount = coerceOpaqueBlendMaterials(clone);
      const stripResult = stripMorphTargetsFromScene(clone);
      const afterStats =
        opaqueBlendMaterialCount > 0 || stripResult.strippedMeshCount > 0
          ? collectSceneAssetStats(clone)
          : beforeStats;
      const stripDurationMs = getDurationMs(stripStartedAt);

      pushCartographerDebugEvent('model.load.asset.stats', {
        loadGeneration,
        reason,
        loadElapsedMs: getLoadElapsedMs(),
        stripDurationMs,
        opaqueBlendMaterialCount,
        beforeStats,
        afterStats,
      });

      if (stripResult.strippedMeshCount > 0) {
        pushCartographerDebugEvent('model.load.morphTargets.omitted', {
          loadGeneration,
          reason,
          loadElapsedMs: getLoadElapsedMs(),
          stripDurationMs,
          stripResult,
        });
      }

      return stripResult;
    }

    function onSceneLoaded(
      scene: THREE.Group,
      fbxUpAxisMetadata?: FbxUpAxisMetadata | null,
      renderAssetInfo: LoadedRenderAssetInfo | null = null,
    ) {
      // Ignore completions from a superseded load (e.g. Strict Mode remount or
      // effect re-run). Otherwise a late auto-mapping can overwrite Import / Save.
      if (loadGeneration !== modelLoadGenerationRef.current) {
        pushCartographerDebugEvent('model.load.scene.stale', {
          loadGeneration,
          activeGeneration: modelLoadGenerationRef.current,
        });
        return;
      }
      pushCartographerDebugEvent('model.load.scene.ready', {
        loadGeneration,
        childCount: scene.children.length,
        renderAssetInfo,
      });

      const cloneStartedAt = performance.now();
      const clone = SkeletonUtils.clone(scene) as THREE.Group;
      enableShadowsOnScene(clone);
      pushCartographerDebugEvent('model.load.scene.cloned', {
        loadGeneration,
        loadElapsedMs: getLoadElapsedMs(),
        cloneDurationMs: getDurationMs(cloneStartedAt),
      });

      let extractionStartedAt: number | null = null;
      try {
        extractionStartedAt = performance.now();
        const {
          skeleton,
          weightsInfo,
          orientationCorrection,
          outlinerHierarchy,
          meshArmatureUniformScale,
        } = extractSkeleton(scene, file?.name, { fbxUpAxisMetadata });
        pushCartographerDebugEvent('model.load.extractSkeleton.completed', {
          loadGeneration,
          loadElapsedMs: getLoadElapsedMs(),
          extractionDurationMs: getDurationMs(extractionStartedAt),
          boneCount: skeleton.bones.length,
        });

        // orientationCorrection is used for the Cartographer viewer's cloned
        // scene, where viewerPoseBridge applies delta-based posing against the
        // corrected scene root. Mini/modal BVH previews load FBX through
        // Player.Character without overriding FBXLoader's scene-root rotation.

        if (orientationCorrection) {
          const rotMatrix = new THREE.Matrix4().makeRotationFromQuaternion(
            orientationCorrection,
          );
          // Apply orientation correction at the cloned scene root rather than
          // mutating SkinnedMesh bind matrices. Root-space correction keeps
          // bind/rest relationships intact so FBX posing deforms cleanly.
          clone.applyMatrix4(rotMatrix);
          clone.updateMatrixWorld(true);
          console.log(
            '[Cartographer] Applied orientation correction on cloned scene root',
          );
        } else {
          console.log('[Cartographer] No orientation correction needed');
        }

        // extractSkeleton may scale rootParentWorld when FBX armature vs mesh
        // bounds diverge; the THREE scene must use the same uniform scale or
        // the mesh stays oversized while internal skeleton math is corrected.
        if (
          meshArmatureUniformScale != null &&
          Number.isFinite(meshArmatureUniformScale) &&
          Math.abs(meshArmatureUniformScale - 1) > 1e-6
        ) {
          clone.scale.multiplyScalar(meshArmatureUniformScale);
          clone.updateMatrixWorld(true);
          console.log(
            `[Cartographer] Applied mesh/armature uniform scale to scene: ${meshArmatureUniformScale.toFixed(6)}`,
          );
        }

        // DEBUG: log scene root quaternion
        const sceneQuaternion = scene.quaternion;
        console.log(
          `[Cartographer] Scene root quaternion: [${sceneQuaternion.x.toFixed(3)}, ${sceneQuaternion.y.toFixed(3)}, ${sceneQuaternion.z.toFixed(3)}, ${sceneQuaternion.w.toFixed(3)}]`,
        );

        stripDisplaySceneMorphTargets(clone, 'skeleton');
        store.setOutlinerHierarchy(outlinerHierarchy);
        store.setGltfScene(clone);
        store.setRenderAssetInfo(renderAssetInfo);
        store.setSkeleton(skeleton);
        store.setRecognitionWeightsInfo(weightsInfo);

        const queuedImportJson = pendingMappingImportJsonRef.current;
        const strictCharacterConfig = characterConfigImportStrictRef.current;
        pendingMappingImportJsonRef.current = null;
        characterConfigImportStrictRef.current = false;

        const runAutoMapping = () => {
          try {
            store.runMappingRecognition();
          } catch (recErr) {
            console.warn('Recognition failed:', recErr);
          }
        };

        if (queuedImportJson) {
          try {
            const { mapping, boneProperties } =
              importMappingJson(queuedImportJson);
            store.applyImportedMapping(mapping, boneProperties);
          } catch (parseErr) {
            console.warn('Queued mapping import failed:', parseErr);
            store.setError(
              parseErr instanceof Error
                ? parseErr.message
                : 'Failed to import mapping',
            );
            if (!strictCharacterConfig) {
              runAutoMapping();
            }
          }
        } else if (strictCharacterConfig) {
          store.setError('Character config was not loaded.');
        } else {
          runAutoMapping();
        }
      } catch (e) {
        pushCartographerDebugEvent('model.load.extractSkeleton.error', {
          loadGeneration,
          loadElapsedMs: getLoadElapsedMs(),
          extractionDurationMs:
            extractionStartedAt == null
              ? null
              : getDurationMs(extractionStartedAt),
          error: stringifyDebugReason(e),
        });
        // Extraction failed — still show the scene without skeleton
        pendingMappingImportJsonRef.current = null;
        characterConfigImportStrictRef.current = false;
        stripDisplaySceneMorphTargets(clone, 'fallback');
        store.setGltfScene(clone);
        store.setRenderAssetInfo(renderAssetInfo);
        store.setOutlinerHierarchy(null);
        console.warn('Skeleton extraction skipped:', e);
        noticesStore.warning(
          'No bones found in the scene. Please try a different rigged character',
        );
      }

      store.setLoading(false);
      pushCartographerDebugEvent('model.load.completed', {
        loadGeneration,
        loadDurationMs: getLoadElapsedMs(),
        hasSkeleton: Boolean(store.getSkeleton()),
      });
      if (url && objectUrlRef.current === url) {
        URL.revokeObjectURL(url);
        objectUrlRef.current = null;
      }
    }

    function onError(err: unknown) {
      if (loadGeneration !== modelLoadGenerationRef.current) {
        pushCartographerDebugEvent('model.load.error.stale', {
          loadGeneration,
          activeGeneration: modelLoadGenerationRef.current,
        });
        return;
      }
      pushCartographerDebugEvent('model.load.error', {
        loadGeneration,
        loadDurationMs: getLoadElapsedMs(),
        error: stringifyDebugReason(err),
      });
      setPendingLoadedCharacterBaseline(null);
      const msg = err instanceof Error ? err.message : 'Failed to load model';
      store.setError(msg);
      store.setLoading(false);
      if (url && objectUrlRef.current === url) {
        URL.revokeObjectURL(url);
        objectUrlRef.current = null;
      }
    }

    if (isFBX) {
      const loader = new CartographerFBXLoader(
        undefined,
        CARTOGRAPHER_FBX_LOAD_OPTIONS,
      );
      const readStartedAt = performance.now();
      file
        .arrayBuffer()
        .then(async (buffer) => {
          pushCartographerDebugEvent('model.load.file.read', {
            loadGeneration,
            format: 'fbx',
            loadElapsedMs: getLoadElapsedMs(),
            readDurationMs: getDurationMs(readStartedAt),
            byteLength: buffer.byteLength,
          });
          await waitForNextPaint();
          if (loadGeneration !== modelLoadGenerationRef.current) {
            pushCartographerDebugEvent('model.load.parse.skipped.stale', {
              loadGeneration,
              activeGeneration: modelLoadGenerationRef.current,
            });
            return;
          }
          const parseStartedAt = performance.now();
          const scene = loader.parse(buffer, '') as THREE.Group;
          const fbxLoadInfo = getCartographerFbxLoadInfo(scene);
          const renderAssetInfo = toLoadedRenderAssetInfo(fbxLoadInfo);
          pushCartographerDebugEvent('model.load.asset.parsed', {
            loadGeneration,
            format: 'fbx',
            loadElapsedMs: getLoadElapsedMs(),
            parseDurationMs: getDurationMs(parseStartedAt),
            loadOptions: CARTOGRAPHER_FBX_LOAD_OPTIONS,
            fbxLoadInfo,
          });
          const enrichStartedAt = performance.now();
          const { warnings, upAxisMetadata } =
            await prepareCartographerFbxScene(scene, buffer);
          pushCartographerDebugEvent('model.load.asset.enriched', {
            loadGeneration,
            format: 'fbx',
            loadElapsedMs: getLoadElapsedMs(),
            enrichDurationMs: getDurationMs(enrichStartedAt),
            warningCount: warnings.length,
            fbxLoadInfo,
          });
          const axisHeuristicWarning =
            getFbxAxisHeuristicWarning(upAxisMetadata);
          const combinedWarnings = axisHeuristicWarning
            ? [...warnings, axisHeuristicWarning]
            : warnings;
          onSceneLoaded(scene, upAxisMetadata, renderAssetInfo);
          if (combinedWarnings.length > 0 && !store.state.error) {
            store.setImportWarnings(
              getFbxImportWarningSummary(combinedWarnings),
              combinedWarnings,
            );
          }
        })
        .catch(onError);
    } else {
      if (!url) {
        onError('Failed to create model URL');
        return undefined;
      }
      const loader = new GLTFLoader();
      const gltfLoadStartedAt = performance.now();
      loader
        .loadAsync(url)
        .then((gltf) => {
          pushCartographerDebugEvent('model.load.asset.parsed', {
            loadGeneration,
            format: 'gltf',
            loadElapsedMs: getLoadElapsedMs(),
            loadAndParseDurationMs: getDurationMs(gltfLoadStartedAt),
          });
          onSceneLoaded(gltf.scene);
        })
        .catch(onError);
    }

    return () => {
      // Invalidate any loader completion still in flight (e.g. React Strict
      // Mode remount) so it cannot run auto-mapping after cleanup.
      pushCartographerDebugEvent('model.load.cleanup', {
        loadGeneration,
      });
      modelLoadGenerationRef.current += 1;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [fileVersion]); // eslint-disable-line react-hooks/exhaustive-deps -- load keyed by fileVersion; `store` retriggers duplicate loads

  useEffect(() => {
    const element = fileNameMeasureRef.current;
    if (!element) return;

    const measure = () => {
      const nextWidth = element.getBoundingClientRect().width;
      setFileNameTextWidth((prevWidth) =>
        Math.abs(prevWidth - nextWidth) < 0.5 ? prevWidth : nextWidth,
      );
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(element);

    return () => observer.disconnect();
  }, [fileName]);

  const detailPanelContent = hasRenderableSkeleton ? (
    isHistoryActive ? (
      <CartographerHistoryPanel
        historyItems={historyItems}
        historyIndex={historyIndex}
        onSelectHistory={(index) => store.jumpToHistory(index)}
        onUndo={handleUndo}
        onRedo={handleRedo}
        undoDisabled={isUndoDisabled}
        redoDisabled={isRedoDisabled}
      />
    ) : isBoneAssignmentsActive ? (
      <BoneAssignments
        skeleton={skeleton}
        mapping={mapping}
        mode={boneAssignmentsMode}
        selectedBoneIndex={selectedBoneIndex}
        selectionVersion={selectionVersion}
        clearSelectionToken={boneAssignmentsClearSelectionToken}
        onModeChange={handleBoneAssignmentsModeChange}
        onAssignRole={handleAssignRole}
        onSelectBone={handleBoneAssignmentsBoneSelect}
        onSetChainFromStartEnd={handleSetChainFromStartEnd}
        onClearChain={(chainName) =>
          recordLegModeAwareMappingHistoryAction('Clear chain', () =>
            store.clearBoneChain(chainName),
          )
        }
        onTitleChange={setBoneAssignmentsTitle}
        onViewerRoleTagChange={setBoneAssignmentsViewerRoleTag}
        onViewerFocusChange={setBoneAssignmentsViewerFocus}
        onViewerHoveredChainChange={setBoneAssignmentsHoveredChain}
        onViewerPickModeChange={handleBoneAssignmentsViewerPickModeChange}
      />
    ) : (
      <Inspector
        skeleton={skeleton}
        outlinerHierarchy={outlinerHierarchy}
        selectedOutlinerNodeId={selectedOutlinerNodeId}
        mapping={mapping}
        selectedBoneIndex={selectedBoneIndex}
        excludedBoneRefs={effectiveExcludedBoneRefs}
        chainSummaries={chainSummaries}
        presetName={presetName}
        poseVersion={poseVersion}
        onBonePropertiesChange={handleBonePropertiesChange}
        onRecordBonePropertiesHistoryAction={recordBonePropertiesHistoryAction}
        onBeginBonePropertiesHistoryTransaction={
          beginBonePropertiesHistoryTransaction
        }
        onRecordPoseHistoryAction={recordPoseHistoryAction}
        onBeginPoseHistoryTransaction={beginPoseHistoryTransaction}
        onCommitHistoryTransaction={commitHistoryTransaction}
        onBonePoseTransformChange={(boneIndex, transform) =>
          store.setBonePoseTransform(boneIndex, transform)
        }
        onResetBonePoseTransform={(boneIndex) =>
          store.resetBonePoseTransform(boneIndex)
        }
        onToggleBoneExcluded={(boneRef) =>
          recordLegModeAwareMappingHistoryAction(
            'Toggle bone exclusion',
            () => store.toggleBoneExcluded(boneRef),
          )
        }
        onChainSolverTypeChange={(chainName, solverType) =>
          recordMappingHistoryAction('Change chain solver', () =>
            store.setChainSolverType(chainName, solverType),
          )
        }
        onChainSolverPropertiesChange={(chainName, solverProperties) =>
          recordMappingHistoryAction('Update chain solver settings', () =>
            store.setChainSolverProperties(chainName, solverProperties),
          )
        }
        onJointOffsetChange={(boneRef, rotation) =>
          store.setJointOffsetRotationForBone(boneRef, rotation)
        }
        onCharacterForwardAxisChange={(axis) =>
          recordMappingHistoryAction('Change forward axis', () =>
            store.setCharacterForwardAxis(axis),
          )
        }
        onCharacterUpAxisChange={(axis) =>
          recordMappingHistoryAction('Change up axis', () =>
            store.setCharacterUpAxis(axis),
          )
        }
        onViewerToolModeChange={(mode) => store.setViewerToolMode(mode)}
        onPropertiesToolModeChange={(mode) => store.setPropertiesToolMode(mode)}
      />
    )
  ) : null;
  const detailPanelContentClassName = isBoneAssignmentsActive
    ? 'h-full p-1'
    : isHistoryActive
      ? 'h-full p-1.5'
      : 'h-full p-2';
  const outlinerContent = hasRenderableSkeleton ? (
    <Outliner
      skeleton={skeleton!}
      hierarchy={outlinerHierarchy}
      mapping={mapping}
      assignedBoneIndexSet={assignedBoneIndexSet}
      selectedNodeId={selectedOutlinerNodeId}
      selectedBoneIndex={selectedBoneIndex}
      selectionVersion={selectionVersion}
      excludedBoneRefs={effectiveExcludedBoneRefs}
      {...outlinerProps}
      propertiesVersion={bonePropertiesVersion}
      onSelectNode={handleOutlinerNodeSelect}
      onToggleBoneExcluded={(boneRef) =>
        recordLegModeAwareMappingHistoryAction(
          'Toggle bone exclusion',
          () => store.toggleBoneExcluded(boneRef),
        )
      }
      onOverrideBoneRole={handleAssignRole}
      onClearBoneChain={(chainName) =>
        recordLegModeAwareMappingHistoryAction('Clear chain', () =>
          store.clearBoneChain(chainName),
        )
      }
      onSetChainFromStartEnd={handleSetChainFromStartEnd}
      onAddBoneToChain={handleAddBoneToChain}
      onRemoveBoneFromChain={(chainName, boneRef) =>
        recordLegModeAwareMappingHistoryAction('Remove bone from chain', () =>
          store.removeBoneFromChain(chainName, boneRef),
        )
      }
      onMinContentWidthChange={handleOutlinerMinContentWidthChange}
      onViewerPickModeChange={handleOutlinerViewerPickModeChange}
    />
  ) : null;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="relative shrink-0">
        <CanvasLayoutHeaderContent slot="start">
          <Link to="/" aria-label="All applets">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              icon={<HomeIcon />}
              tabIndex={-1}
              aria-hidden
            />
          </Link>
        </CanvasLayoutHeaderContent>
        <div className="pointer-events-none absolute top-0 left-0 -z-10 h-0 overflow-hidden opacity-0">
          <div ref={fileNameMeasureRef} className="inline-flex">
            <Text variant="studio/title/md" className="whitespace-nowrap">
              {fileName ?? ''}
            </Text>
          </div>
        </div>
        <input
          ref={mappingInputRef}
          type="file"
          accept=".json"
          className="hidden"
          aria-hidden
          onChange={handleMappingFileChange}
        />
        {shouldAttemptHeaderFileName && fileName && (
          <CanvasLayoutHeaderContent
            slot="center"
            minVisibleWidth={
              fileNameLayoutWidth + HEADER_FILENAME_HIDE_BUFFER_PX
            }
            onVisibilityChange={setIsHeaderFileNameVisible}
          >
            <div
              className="flex max-w-full min-w-0 items-center justify-center overflow-hidden"
              style={{ width: fileNameLayoutWidth }}
            >
              <EditableCharacterFileName
                fileName={fileName}
                editable={isHeaderFileNameEditable}
                disabled={saveInProgress}
                onRename={handleCharacterFileRename}
              />
            </div>
          </CanvasLayoutHeaderContent>
        )}
        {showEditorChrome && (
          <CanvasLayoutHeaderContent slot="end">
            <div className="flex shrink-0 flex-nowrap items-center gap-2">
              {showTopSaveButton && (
                <Tooltip text={topSaveButtonLabel} side="bottom" inModal>
                  <button
                    type="button"
                    onClick={() => {
                      void handleSave();
                    }}
                    disabled={topSaveButtonDisabled}
                    className={`focus-visible inline-flex h-[30px] items-center rounded-lg border px-1.5 shadow-md backdrop-blur-sm transition-colors ${
                      topSaveButtonDisabled
                        ? 'border-gray-30 bg-gray-15 text-gray-45 dark:border-gray-60 dark:bg-gray-75 dark:text-gray-30 cursor-not-allowed opacity-70'
                        : 'cursor-pointer'
                    } ${
                      showActionLabels
                        ? `gap-1.5 ${actionAlignIcon === 'end' ? 'flex-row-reverse' : ''}`
                        : 'justify-center'
                    } ${
                      topSaveButtonDisabled
                        ? ''
                        : 'border-gray-90 bg-gray-90 text-gray-15 hover:border-gray-75 hover:bg-gray-75 dark:border-gray-15 dark:bg-gray-15 dark:text-gray-90 dark:hover:border-gray-30 dark:hover:bg-gray-30'
                    }`}
                    aria-label={topSaveButtonLabel}
                    title={showActionLabels ? undefined : topSaveButtonLabel}
                  >
                    {showActionLabels ? (
                      <Text as="span" variant="web/body/medium/md">
                        {topSaveButtonLabel}
                      </Text>
                    ) : (
                      <CheckmarkIcon className="h-4 w-4" />
                    )}
                  </button>
                </Tooltip>
              )}
              <Tooltip text="Character" side="bottom" inModal>
                <button
                  type="button"
                  onClick={handleToggleCharacterDrawer}
                  className={`focus-visible inline-flex h-[30px] cursor-pointer items-center justify-center rounded-md px-1.5 transition-opacity duration-150 hover:opacity-80 ${
                    showCharacterDrawer
                      ? 'bg-gray-15 dark:bg-gray-75'
                      : 'bg-transparent'
                  } text-gray-90 dark:text-gray-15`}
                  aria-label="Toggle character panel"
                  aria-pressed={showCharacterDrawer}
                >
                  <CharacterIcon className="h-5 w-5" />
                </button>
              </Tooltip>
              <Dropdown
                align="end"
                width={240}
                inModal
                triggerTooltip="Cartographer Menu"
                triggerTooltipSide="bottom"
                trigger={
                  <button
                    type="button"
                    className="focus-visible text-gray-90 dark:text-gray-15 inline-flex h-[30px] cursor-pointer items-center justify-center rounded-md px-1.5 transition-opacity duration-150 hover:opacity-80"
                    aria-label="Cartographer Menu"
                  >
                    <HamburgerIcon className="h-6 w-6" />
                  </button>
                }
              >
                {showMappingActions && (
                  <>
                    <Dropdown.Item
                      icon={<DownloadIcon />}
                      disabled={!hasMapping}
                      onClick={() => store.exportMapping()}
                    >
                      Export mapping
                    </Dropdown.Item>
                    <Dropdown.Item
                      icon={<DownloadIcon className="rotate-180" />}
                      disabled={!hasMapping}
                      onClick={handleImportMapping}
                    >
                      Import mapping
                    </Dropdown.Item>
                  </>
                )}
                {(hasGltfScene || hasEverLoadedScene.current) && (
                  <>
                    {showMappingActions && <Dropdown.Separator />}
                    {showMenuSaveButton && (
                      <Dropdown.Item
                        icon={<CheckmarkIcon className="h-4 w-4" />}
                        disabled={menuSaveButtonDisabled}
                        onClick={() => {
                          void handleSave();
                        }}
                      >
                        {menuSaveButtonLabel}
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      icon={<CopyIcon />}
                      disabled={menuSaveButtonDisabled}
                      onClick={() => {
                        void handleSaveAsNew();
                      }}
                    >
                      {menuSaveAsNewButtonLabel}
                    </Dropdown.Item>
                    <Dropdown.Item
                      icon={<UndoIcon className="h-4 w-4" />}
                      disabled={isUndoDisabled}
                      onClick={handleUndo}
                    >
                      Undo
                    </Dropdown.Item>
                    <Dropdown.Item
                      icon={<RedoIcon className="h-4 w-4" />}
                      disabled={isRedoDisabled}
                      onClick={handleRedo}
                    >
                      Redo
                    </Dropdown.Item>
                  </>
                )}
                <Dropdown.Separator />
                <Dropdown.Item
                  icon={<HelpIcon />}
                  onClick={() => {
                    window.open(
                      CARTOGRAPHER_DOCS_URL,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }}
                >
                  Documentation
                </Dropdown.Item>
                <Dropdown.Item
                  icon={<LinkIcon />}
                  disabled={!loadedCharacterID}
                  onClick={handleCopyCharacterId}
                >
                  Copy character ID
                </Dropdown.Item>
                {activeCharacterFileSizeLabel && (
                  <>
                    <Dropdown.Separator />
                    <div className="flex justify-end gap-1.5 px-3 py-1.5 text-right">
                      <Text
                        variant="studio/body/medium/xs"
                        className="text-secondary"
                      >
                        Loaded file size
                      </Text>
                      <Text
                        variant="studio/body/medium/xs"
                        className="text-primary tabular-nums"
                      >
                        {activeCharacterFileSizeLabel}
                      </Text>
                    </div>
                    {activeRenderLodLabel && (
                      <div className="flex justify-end gap-1.5 px-3 py-1.5 text-right">
                        <Text
                          variant="studio/body/medium/xs"
                          className="text-secondary"
                        >
                          Render LOD
                        </Text>
                        <Text
                          variant="studio/body/medium/xs"
                          className="text-primary tabular-nums"
                        >
                          {activeRenderLodLabel}
                        </Text>
                      </div>
                    )}
                  </>
                )}
              </Dropdown>
              {showOpenInMotionEditorButton ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={!apiKey || openingMotionEditor}
                  loading={openingMotionEditor}
                  onClick={() => {
                    void handleOpenInMotionEditor();
                  }}
                >
                  Open in Motion Editor
                </Button>
              ) : null}
            </div>
          </CanvasLayoutHeaderContent>
        )}
      </div>

      {motionPreviewModal}
      {motionActorPickerModal}

      <Modal
        open={pendingExcludedAssignment !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingExcludedAssignment(null);
          }
        }}
        title={excludedAssignmentPromptTitle}
        description={excludedAssignmentPromptDescription}
        maxWidth={520}
      >
        <div className="flex flex-none justify-end gap-2 pt-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPendingExcludedAssignment(null)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirmExcludedAssignment}
          >
            Include and Assign
          </Button>
        </div>
      </Modal>

      <Modal
        open={loadCharacterUnsavedModalOpen}
        onOpenChange={(open) => {
          if (loadCharacterSaveInProgress) return;
          setLoadCharacterUnsavedModalOpen(open);
          if (!open) {
            clearPendingCharacterLoadAction();
          }
        }}
        title="Unsaved character changes"
        description={
          <Text
            variant="studio/body/medium/sm"
            className="text-gray-60 text-[16px]/[20px]"
          >
            Save now to keep changes before loading{' '}
            <Text as="span" variant="studio/body/bold/sm">
              {pendingCharacterLoadTargetLabel}
            </Text>
            .
          </Text>
        }
        maxWidth={520}
      >
        <div className="flex flex-none justify-end gap-2 pt-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setLoadCharacterUnsavedModalOpen(false);
              clearPendingCharacterLoadAction();
            }}
            disabled={loadCharacterSaveInProgress}
          >
            Cancel
          </Button>
          <Button
            variant="secondary-destructive"
            size="sm"
            onClick={handleContinueLoadingCharacterWithoutSaving}
            disabled={loadCharacterSaveInProgress}
          >
            Don&apos;t save
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              void handleSaveAndLoadCharacter();
            }}
            disabled={loadCharacterSaveInProgress || saveInProgress || !apiKey}
          >
            {loadCharacterSaveInProgress ? 'Saving…' : 'Save and load'}
          </Button>
        </div>
      </Modal>

      {motionUnsavedChangesModal}

      <CharacterLibraryLoadModal
        open={loadCharacterModalOpen}
        onOpenChange={setLoadCharacterModalOpen}
        apiKey={apiKey}
        characters={charactersWithConfig}
        selectedCharacterId={loadedCharacterID}
        charactersLoading={charactersLoading}
        charactersError={charactersError}
        loadingCharacterId={loadingCharacterId}
        onSelectCharacter={handleSelectCharacterWithPrompt}
        onDeleteCharacter={handleDeleteLibraryCharacter}
        onRenameCharacter={handleRenameLibraryCharacter}
      />

      <Modal
        open={isImportWarningDetailsOpen}
        onOpenChange={setIsImportWarningDetailsOpen}
        title="Import warnings"
        description="Some imported data needs attention."
        maxWidth={640}
      >
        <div className="space-y-3">
          <div className="border-primary bg-primary max-h-[320px] overflow-y-auto rounded-lg border">
            <ul className="divide-gray-30 dark:divide-gray-60 divide-y">
              {importWarningDetails.map((detail) => {
                const contextPreview =
                  detail.contexts.length > 2
                    ? `${detail.contexts.slice(0, 2).join(', ')}, +${detail.contexts.length - 2} more`
                    : detail.contexts.join(', ');
                return (
                  <li key={detail.label} className="space-y-1 px-3 py-2">
                    <Text
                      variant="studio/body/medium/sm"
                      className="text-primary break-all"
                    >
                      {detail.label}
                    </Text>
                    <Text variant="studio/body/sm" className="text-secondary">
                      {getImportWarningReasonText(detail.reason)}
                      {contextPreview ? ` (${contextPreview})` : ''}
                    </Text>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsImportWarningDetailsOpen(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <div
        className="box-border flex min-h-0 flex-1 overflow-hidden"
        style={CARTOGRAPHER_VIEWER_WORKSPACE_STYLE}
      >
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {error && (
            <div className="shrink-0 rounded-md bg-red-50 px-3">
              <div className="flex min-h-[34px] items-center gap-2">
                <Text
                  variant="studio/body/medium/sm"
                  className="min-w-0 flex-1 leading-tight text-red-700"
                >
                  {error}
                </Text>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={<CrossIcon className="text-red-700" />}
                    className="hover:bg-red-20/70 text-red-700"
                    aria-label="Dismiss error message"
                    onClick={() => store.setError(null)}
                  />
                </div>
              </div>
            </div>
          )}

          {showAccountAccessBanner && (
            <div className="border-secondary shrink-0 rounded-md border border-blue-200/90 bg-blue-50 px-3">
              <div className="flex min-h-[34px] items-center gap-2">
                <a
                  href={
                    showAccountSignInBanner
                      ? accountAccessLoginUrl
                      : CARTOGRAPHER_ACCOUNT_UPGRADE_URL
                  }
                  className="text-gray-90 min-w-0 flex-1 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-blue-400/80"
                >
                  <Text
                    variant="studio/body/medium/sm"
                    className="text-gray-90 decoration-blue-60 hover:decoration-blue min-w-0 leading-tight underline underline-offset-2"
                  >
                    {showAccountSignInBanner
                      ? 'Sign in to save characters to your library and use connected features — click here to continue.'
                      : 'You need to upgrade your plan in order to save characters — click here for plans.'}
                  </Text>
                </a>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={<CrossIcon className="text-gray-90" />}
                    className="text-gray-90 hover:bg-white/40"
                    aria-label="Dismiss account notice"
                    onClick={
                      showAccountSignInBanner
                        ? dismissAccountSignInBanner
                        : dismissAccountUpgradeBanner
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {warning && (
            <div className="border-secondary shrink-0 rounded-md border bg-yellow-50 px-3">
              <div className="flex min-h-[34px] items-center gap-2">
                <Text
                  variant="studio/body/medium/sm"
                  className="text-gray-90 min-w-0 flex-1 leading-tight"
                >
                  {warning}
                </Text>
                <div className="flex shrink-0 items-center gap-1">
                  {importWarningDetails.length > 0 && (
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon={<InfoIcon className="text-gray-90" />}
                      className="text-gray-90 hover:bg-white/35"
                      onClick={() => setIsImportWarningDetailsOpen(true)}
                    >
                      Info
                    </Button>
                  )}
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={<CrossIcon className="text-gray-90" />}
                    className="text-gray-90 hover:bg-white/35"
                    aria-label="Dismiss warning message"
                    onClick={() => {
                      setIsImportWarningDetailsOpen(false);
                      store.setWarning(null);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {loading && !hasEverLoadedScene.current && (
            <CartographerLayoutStateScaffold isAnimated>
              <div className="flex h-full items-center justify-center px-6">
                <Text variant="studio/body/md" className="text-primary">
                  Loading model…
                </Text>
              </div>
            </CartographerLayoutStateScaffold>
          )}

          {showWelcomeScreen && (
            <CartographerWelcomeScreen
              apiKey={apiKey}
              characters={charactersWithConfig}
              charactersLoading={charactersLoading}
              charactersError={charactersError}
              loadingCharacterId={loadingCharacterId}
              onSelectCharacter={handleSelectCharacterWithPrompt}
              onLoadExistingCharacter={() => setLoadCharacterModalOpen(true)}
              onModelFileSelected={handleModelFileSelectedWithPrompt}
            />
          )}

          {(hasGltfScene || hasEverLoadedScene.current) && (
            <div
              className="border-gray-30/60 dark:border-gray-60/60 bg-primary relative min-h-0 flex-1 overflow-hidden rounded-3xl border focus:outline-none"
              data-cartographer-viewer-area="true"
              tabIndex={0}
              onPointerEnter={() => setIsViewerHovered(true)}
              onPointerLeave={() => setIsViewerHovered(false)}
              onFocus={() => setIsViewerFocused(true)}
              onBlur={(event) => {
                const nextFocusedElement = event.relatedTarget;
                if (
                  nextFocusedElement instanceof Node &&
                  event.currentTarget.contains(nextFocusedElement)
                ) {
                  return;
                }
                setIsViewerFocused(false);
              }}
              onPointerDownCapture={(event) => {
                if (event.currentTarget !== document.activeElement) {
                  event.currentTarget.focus({ preventScroll: true });
                }
              }}
            >
              <Viewer3D
                gltfScene={hasGltfScene ? store.getGltfScene() : null}
                autoFrameToken={fileVersion}
                autoFrameReady={!loading}
                forceTransitionBlur={isCharacterSwitchActive}
                suppressBoneNameLabels={motionStagePlaybackActive}
                skeleton={skeleton}
                mapping={mapping}
                forcedRoleTag={boneAssignmentsViewerRoleTag}
                boneAssignmentsViewerFocus={boneAssignmentsViewerFocus}
                hoveredBoneAssignmentChain={boneAssignmentsHoveredChain}
                boneAssignmentsViewerPickMode={activeViewerPickMode}
                assignedBoneIndexSet={assignedBoneIndexSet}
                excludedBoneRefs={effectiveExcludedBoneRefs}
                visibility={visibility}
                boneColorPreset={boneColorPreset}
                boneRadiusScale={boneRadiusScale}
                chainThicknessScale={chainThicknessScale}
                manipulatorScale={manipulatorScale}
                propertiesVersion={bonePropertiesVersion}
                poseVersion={poseVersion}
                cameraFollowMotion={cameraFollowMotion}
                focusRequest={viewerFocusRequest}
                gridCellSize={gridCellSize}
                selectedBoneIndex={selectedBoneIndex}
                onSelectBone={handleViewerBoneSelect}
                onSelectBoneFromPropertiesBadge={
                  handleViewerPropertiesBadgeSelect
                }
                viewerToolMode={viewerToolMode}
                rotateSpaceMode={rotateSpaceMode}
                propertiesToolMode={propertiesToolMode}
                onBonePropertiesChange={handleBonePropertiesChange}
                onJointOffsetChange={handleJointOffsetChange}
                onApplyBoneRotateFromWorldQuaternion={
                  handleApplyBoneRotateFromWorldQuaternion
                }
                onApplyBoneRotateFromGimbalDelta={
                  handleApplyBoneRotateFromGimbalDelta
                }
                onSetBoneEulerRotation={handleSetBoneEulerRotation}
                onRotateGizmoDraggingChange={handleRotateGizmoDraggingChange}
                onPropertiesManipulatorDraggingChange={
                  handlePropertiesManipulatorDraggingChange
                }
                screenshotRef={screenshotRef}
              />

              <div className="pointer-events-none absolute inset-3 z-20">
                <div className="relative h-full w-full">
                  <div
                    className={`pointer-events-auto absolute top-0 z-40 ${
                      usesCombinedLayout
                        ? 'right-0'
                        : 'left-1/2 -translate-x-1/2'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <ViewerToolbar
                        visibility={visibility}
                        menuMode={toolbarMenuMode}
                        boneColorPreset={boneColorPreset}
                        boneRadiusScale={boneRadiusScale}
                        chainThicknessScale={chainThicknessScale}
                        manipulatorScale={manipulatorScale}
                        cameraFollowMotion={cameraFollowMotion}
                        showCameraFollowMotionButton={Boolean(
                          motionStagePreview,
                        )}
                        showVisibilityLabel={showToolbarTriggerLabels}
                        showSettingsLabel={showToolbarTriggerLabels}
                        onToggle={handleViewerVisibilityToggle}
                        onBoneColorPresetChange={(preset) =>
                          store.setBoneColorPreset(preset)
                        }
                        onBoneRadiusScaleChange={(value) =>
                          store.setBoneRadiusScale(value)
                        }
                        onChainThicknessScaleChange={(value) =>
                          store.setChainThicknessScale(value)
                        }
                        onManipulatorScaleChange={(value) =>
                          store.setManipulatorScale(value)
                        }
                        onCameraFollowMotionChange={(enabled) =>
                          store.setCameraFollowMotion(enabled)
                        }
                        onMenuModeChange={(mode) =>
                          store.setToolbarMenuMode(mode)
                        }
                      />
                      {isOffsetPosePreviewActive && (
                        <Tooltip
                          side="bottom"
                          inModal
                          text={
                            <>
                              Showing the character with post-retarget offsets
                              applied.
                              <br />
                              Leave Offset mode to return to the default pose.
                            </>
                          }
                        >
                          <div className="border-gray-60/80 dark:border-gray-45/80 bg-gray-90/85 pointer-events-auto cursor-help rounded-full border px-3 py-1 backdrop-blur-md dark:bg-black/80">
                            <Text
                              variant="studio/body/medium/xs"
                              className="whitespace-nowrap text-white"
                            >
                              Offset Pose Preview
                            </Text>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <div className="pointer-events-none absolute inset-0 z-30 flex items-end justify-center p-0">
                    <div className="flex w-full flex-col items-center gap-2">
                      {motionStagePreview}
                      <div
                        className={
                          cartographerAssetLibraryMode === 'motions'
                            ? 'pointer-events-auto mx-auto flex h-[min(82vh,960px)] min-h-[560px] w-full max-w-[1080px] min-w-0 flex-col sm:min-h-[520px]'
                            : 'pointer-events-auto mx-auto h-auto w-fit max-w-full'
                        }
                      >
                        <FrontDoor.Container
                          ref={cartographerFrontDoorRef}
                          showBackground={
                            cartographerAssetLibraryMode === 'motions'
                          }
                          className={
                            cartographerAssetLibraryMode === 'motions'
                              ? 'h-full! min-h-0 w-full min-w-0'
                              : 'h-auto! w-full min-w-0'
                          }
                        >
                          <FrontDoor.FadeContent
                            show={cartographerAssetLibraryMode === 'motions'}
                          >
                            {assetLibraryPanel}
                          </FrontDoor.FadeContent>
                          <FrontDoor.FadeContent
                            show={cartographerAssetLibraryMode === ''}
                            className="w-full"
                          >
                            <div
                              ref={handleViewerToolDockMount}
                              className="relative flex w-full justify-center"
                            >
                              <ViewerToolDock
                                availableWidth={viewportWidth}
                                viewerToolMode={viewerToolMode}
                                rotateSpaceMode={rotateSpaceMode}
                                propertiesToolMode={propertiesToolMode}
                                selectedBonePropertyIndicators={
                                  selectedBonePropertyIndicators
                                }
                                onAnyButtonClick={handleCloseCharacterDrawer}
                                historySection={
                                  showHistoryDockButton
                                    ? {
                                        historyOpen: isHistoryActive,
                                        onHistoryToggle: handleHistoryToggle,
                                      }
                                    : undefined
                                }
                                mappingSection={dockMappingSection}
                                motionSection={motionSection}
                                onViewerToolModeChange={
                                  handleDockViewerToolModeChange
                                }
                                onRotateSpaceModeChange={
                                  handleDockRotateSpaceModeChange
                                }
                                onPropertiesToolModeChange={
                                  handleDockPropertiesToolModeChange
                                }
                              />
                            </div>
                          </FrontDoor.FadeContent>
                        </FrontDoor.Container>
                      </div>
                    </div>
                  </div>

                  <WorkspacePanels
                    hasEverLoadedScene={hasEverLoadedScene.current}
                    hasRenderableSkeleton={hasRenderableSkeleton}
                    hideSidePanels={hideSidePanels}
                    usesCombinedLayout={usesCombinedLayout}
                    detailPanelTitle={detailPanelTitle}
                    detailPanelTitleClassName={detailPanelTitleClassName}
                    detailPanelContent={detailPanelContent}
                    detailPanelContentClassName={detailPanelContentClassName}
                    isBoneAssignmentsActive={isBoneAssignmentsActive}
                    outlinerFooterSettingsControl={
                      outlinerFooterSettingsControl
                    }
                    outlinerContent={outlinerContent}
                    outlinerWidth={outlinerWidth}
                    outlinerHeight={outlinerHeight}
                    isOutlinerOpen={isOutlinerOpen}
                    onToggleOutlinerOpen={() =>
                      setIsOutlinerOpen((open: boolean) => !open)
                    }
                    onOutlinerWidthChange={handleOutlinerWidthChange}
                    onOutlinerHeightChange={setOutlinerHeight}
                    outlinerMinWidth={outlinerMinWidth}
                    outlinerMaxWidth={outlinerMaxWidth}
                    inspectorWidth={inspectorWidth}
                    inspectorHeight={inspectorHeight}
                    isInspectorOpen={isInspectorOpen}
                    onToggleInspectorOpen={() =>
                      setIsInspectorOpen((open: boolean) => !open)
                    }
                    onInspectorWidthChange={handleInspectorWidthChange}
                    onInspectorHeightChange={setInspectorHeight}
                    inspectorMaxWidth={inspectorMaxWidth}
                    activeCombinedTab={activeCombinedTab}
                    onSelectCombinedTab={handleCombinedTabSelect}
                    combinedPanelResolvedWidth={combinedPanelResolvedWidth}
                    combinedPanelHeight={combinedPanelHeight}
                    isCombinedPanelOpen={isCombinedPanelOpen}
                    onToggleCombinedPanelOpen={() =>
                      setIsCombinedPanelOpen((open: boolean) => !open)
                    }
                    onCombinedPanelWidthChange={handleCombinedPanelWidthChange}
                    onCombinedPanelHeightChange={setCombinedPanelHeight}
                    combinedPanelMinWidth={combinedPanelMinWidth}
                    combinedPanelMaxWidth={combinedPanelMaxWidth}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          className="h-full shrink-0 overflow-hidden transition-[width] duration-200"
          style={{ width: showCharacterDrawer ? PANEL_WIDTH : 0 }}
        >
          <div className="bg-primary h-full overflow-hidden rounded-l-3xl">
            <Panel
              isOpen={showCharacterDrawer}
              exitTransitionDuration={CHARACTER_DRAWER_TRANSITION_DURATION_MS}
              title="Character"
              className="flex min-h-0 flex-col overflow-hidden lg:[&>#panel-header>div:first-child]:h-10 lg:[&>#panel-header>div:nth-child(2)]:h-[52px] lg:[&>#panel-header>div:nth-child(2)]:pt-0 [&>div:last-child]:flex [&>div:last-child]:min-h-0 [&>div:last-child]:flex-1 [&>div:last-child]:flex-col [&>div:last-child]:overflow-y-auto [&>div:last-child]:pt-0 [&>div:last-child]:pb-4 lg:[&>div:last-child]:pb-6 [&>div:last-child>div:first-child]:min-h-0 [&>div:last-child>div:first-child]:flex-1"
              closePanel={handleCloseCharacterDrawer}
            >
              <CartographerCharacterPanel
                apiKey={apiKey}
                characters={charactersWithConfig}
                selectedCharacterId={loadedCharacterID}
                charactersLoading={charactersLoading}
                charactersError={charactersError}
                loadingCharacterId={loadingCharacterId}
                headerContent={
                  shouldRenderDrawerFileName && fileName ? (
                    <EditableCharacterFileName
                      fileName={fileName}
                      editable={isHeaderFileNameEditable}
                      disabled={saveInProgress}
                      onRename={handleCharacterFileRename}
                    />
                  ) : null
                }
                onSelectCharacter={handleSelectCharacterFromDrawer}
                onModelFileSelected={handleModelFileSelectedFromDrawer}
                onViewAll={() => setLoadCharacterModalOpen(true)}
                uploadPanelOpen={characterDrawerUploadPanelOpen}
                onUploadPanelOpenChange={setCharacterDrawerUploadPanelOpen}
                uploadPanelTab={characterDrawerUploadPanelTab}
                onUploadPanelTabChange={setCharacterDrawerUploadPanelTab}
                onUploadPanelPreparingChange={handleUploadPanelPreparingChange}
              />
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
